import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useContext, createContext, useMemo } from "react";
import { ArrowRight, ArrowUpRight, Activity, Cpu, Bot, Brain, Network, Video, Menu, X, Sun, Moon } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

const BRAND_MARK = "/brand/svcgroup-mark.svg";
const BRAND_LOCKUP_DARK = "/brand/svcgroup-logo-ai-agents-physical-ai-dark.svg";
const BRAND_LOCKUP_LIGHT_HORIZONTAL = "/brand/svcgroup-logo-ai-agents-physical-ai-horizontal-light.svg";

// Optional cinematic hero clip (e.g. generated with Seedance). If the file is
// missing the <video> errors out silently and the canvas backdrop carries the scene.
const HERO_VIDEO_SRC = "/media/hero-seedance.mp4";

const REDUCED = () =>
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ---------------------------------- theme ---------------------------------- */

const THEME_KEY = "svc-theme";
const ThemeContext = createContext({ theme: "light", toggleTheme: () => {} });

function ThemeProvider({ children }) {
    const [theme, setTheme] = useState(() => {
        if (typeof window === "undefined") return "light";
        const saved = window.localStorage.getItem(THEME_KEY);
        return saved === "dark" ? "dark" : "light";
    });

    useLayoutEffect(() => {
        document.documentElement.setAttribute("data-theme", theme);
        window.localStorage.setItem(THEME_KEY, theme);
    }, [theme]);

    const toggleTheme = useCallback(() => {
        setTheme((t) => (t === "dark" ? "light" : "dark"));
    }, []);

    return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>;
}

function useTheme() {
    return useContext(ThemeContext);
}

function ThemeToggle({ className = "" }) {
    const { theme, toggleTheme } = useTheme();
    const isLight = theme === "light";

    return (
        <button
            type="button"
            onClick={toggleTheme}
            aria-label={isLight ? "Switch to dark mode" : "Switch to light mode"}
            aria-pressed={!isLight}
            data-cursor
            className={`relative flex items-center justify-center w-9 h-9 rounded-full border border-cream/15 text-cream/70 hover:text-cream hover:border-cream/35 transition-colors duration-300 cursor-pointer shrink-0 ${className}`}
        >
            <Moon className={`w-4 h-4 absolute transition-all duration-300 ${isLight ? "opacity-100 rotate-0 scale-100" : "opacity-0 rotate-45 scale-50"}`} />
            <Sun className={`w-4 h-4 absolute transition-all duration-300 ${isLight ? "opacity-0 -rotate-45 scale-50" : "opacity-100 rotate-0 scale-100"}`} />
        </button>
    );
}

// Raw hex for the tiny SVG illustrations inside demo cards (GSAP/canvas props
// can't consume CSS custom properties, so these mirror the current theme by hand).
// `ink` matches whatever the demo panel's own `bg-well` shade is, so
// "blends with background" accents stay correct in both themes.
function useDemoColors() {
    const { theme } = useTheme();
    return useMemo(() => {
        const isLight = theme === "light";
        const creamRgb = isLight ? "23,24,18" : "242,240,233";
        return {
            ink: isLight ? "#E7E3D9" : "#0A0B09",
            cream: isLight ? "#171812" : "#F2F0E9",
            sage: isLight ? "#3A5C44" : "#A3C2A8",
            ember: isLight ? "#B94827" : "#E8663C",
            // Faint foreground/ember-tinted line/fill for subtle background details
            tint: (a) => `rgba(${creamRgb},${a})`,
            emberTint: (a) => `rgba(${isLight ? "185,72,39" : "232,102,60"},${a})`,
        };
    }, [theme]);
}

const DEMO_GRID =
    "bg-[image:linear-gradient(rgb(var(--color-cream)/0.05)_1px,transparent_1px),linear-gradient(90deg,rgb(var(--color-cream)/0.05)_1px,transparent_1px)]";

const caseStudies = [
    {
        num: "00",
        title: "Bldify — A Nine-Agent Operations Brain",
        impact: [
            { k: "Agents in production", v: "9" },
            { k: "Inference location", v: "100% server-side" },
            { k: "High-stakes auto-fire", v: "0 (human-gated)" },
        ],
        summary:
            "We designed, built, and shipped a full operations platform for specialty-trade contractors: vision-based estimating grounded in a governed price book, invoice OCR with duplicate detection, a stream agent that turns field chat into routed approvals, a live voice walkthrough agent, video analysis for measurements and defects, a unified financial ledger with reconciliation, and severity-based incident escalation — all with per-call audit logging, row-level multi-tenancy, and human-in-the-loop on every high-stakes action.",
        href: "/work/bldify-nine-agent-operations",
    },
    {
        num: "01",
        title: "Enterprise AP Automation",
        impact: [
            { k: "Invoice cycle time", v: "−62%" },
            { k: "Touchless rate", v: "78%" },
            { k: "Duplicate catch", v: "99.4%" },
        ],
        summary:
            "Document AI + exception routing across an ERP. Invoices ingested from email and portals, extracted to a typed schema, de-duplicated, and posted; exceptions routed to AP clerks for approval.",
        href: "/work/enterprise-ap-automation",
    },
    {
        num: "02",
        title: "Vision QA + Agentic Rework",
        impact: [
            { k: "First-pass yield", v: "+4.7%" },
            { k: "Scrap", v: "−22%" },
            { k: "Payback", v: "< 6 months" },
        ],
        summary: "Defect detection with an agent that orchestrates rework tasks across MES and WMS.",
    },
    {
        num: "03",
        title: "Underwriting Decision Agent",
        impact: [
            { k: "Quote turnaround", v: "−71%" },
            { k: "Straight-through rate", v: "+46%" },
            { k: "Pricing leakage", v: "−31%" },
        ],
        summary:
            "Quote generation grounded in the rate card, with audit logging and compliance-safe pricing — every number traces to the rate card, never the model.",
    },
    {
        num: "04",
        title: "Global Manufacturer: Vision QA + Agentic Rework",
        impact: [
            { k: "FPY", v: "+4.7%" },
            { k: "Scrap", v: "−22%" },
            { k: "Payback", v: "< 6 months" },
        ],
        summary: "Deployed vision models for defect detection with an agent that orchestrates rework tasks across MES and WMS.",
    },
    {
        num: "05",
        title: "Fortune 100: Enterprise Context Platform",
        impact: [
            { k: "Time-to-answer", v: "−68%" },
            { k: "Ticket deflection", v: "+35%" },
            { k: "Security findings", v: "0 critical" },
        ],
        summary: "Agentic RAG across federated enterprise sources, shared business context, NeMo/NIM-tuned models, and granular RBAC for IT, Finance, and Supply Chain agents.",
    },
    {
        num: "06",
        title: "Logistics Provider: Robotics Pilot",
        impact: [
            { k: "Throughput", v: "+18%" },
            { k: "Incidents", v: "−100%" },
            { k: "Pilot duration", v: "12 weeks" },
        ],
        summary: "Pick-and-place and yard autonomy POCs using computer vision, motion planning, and safety layers with teleop fallback.",
        href: "/work/logistics-physical-ai-pilot",
    },
];

const NAV_LINKS = [
    { href: "#services", label: "Services", aria: "Explore Enterprise AI Consulting Services", id: "services" },
    { href: "#industries", label: "Industries", aria: "View AI Solutions by Industry", id: "industries" },
    { href: "#cases", label: "Case Studies", aria: "Read AI Deployment Case Studies", id: "cases" },
    { href: "#philosophy", label: "Philosophy", aria: "Discover our AI Design Philosophy", id: "philosophy" },
    { href: "#methodology", label: "Methodology", aria: "Learn our AI Integration Methodology", id: "methodology" },
    { href: "#about", label: "About", aria: "Learn About Silicon Valley Consulting Group", id: "about" },
];

/* ---------------------------------- shared ---------------------------------- */

function useMedia(query) {
    const [matches, setMatches] = useState(
        () => typeof window !== "undefined" && window.matchMedia(query).matches
    );
    useEffect(() => {
        const mq = window.matchMedia(query);
        const fn = (e) => setMatches(e.matches);
        mq.addEventListener("change", fn);
        setMatches(mq.matches);
        return () => mq.removeEventListener("change", fn);
    }, [query]);
    return matches;
}

function BrandLogo({ className }) {
    return <img src={BRAND_MARK} alt="" aria-hidden="true" className={`shrink-0 object-contain ${className}`} />;
}

function NoiseOverlay() {
    return (
        <svg className="fixed inset-0 w-full h-full z-50 pointer-events-none opacity-[0.05]" style={{ mixBlendMode: "overlay" }}>
            <filter id="v3NoiseFilter">
                <feTurbulence type="fractalNoise" baseFrequency="0.8" numOctaves="3" stitchTiles="stitch" />
            </filter>
            <rect width="100%" height="100%" filter="url(#v3NoiseFilter)" />
        </svg>
    );
}

function CustomCursor() {
    const dotRef = useRef(null);
    const ringRef = useRef(null);

    useEffect(() => {
        if (!window.matchMedia("(pointer: fine)").matches || REDUCED()) return;
        const dot = dotRef.current;
        const ring = ringRef.current;
        gsap.set([dot, ring], { x: -100, y: -100 });
        const dx = gsap.quickTo(dot, "x", { duration: 0.1, ease: "power2.out" });
        const dy = gsap.quickTo(dot, "y", { duration: 0.1, ease: "power2.out" });
        const rx = gsap.quickTo(ring, "x", { duration: 0.4, ease: "power3.out" });
        const ry = gsap.quickTo(ring, "y", { duration: 0.4, ease: "power3.out" });

        const move = (e) => {
            dx(e.clientX);
            dy(e.clientY);
            rx(e.clientX);
            ry(e.clientY);
        };
        const isInteractive = (t) => t.closest && t.closest("a, button, [data-cursor]");
        const over = (e) => {
            if (isInteractive(e.target)) gsap.to(ring, { scale: 1.9, opacity: 0.9, duration: 0.3, ease: "power2.out" });
        };
        const out = (e) => {
            if (isInteractive(e.target)) gsap.to(ring, { scale: 1, opacity: 1, duration: 0.3, ease: "power2.out" });
        };
        window.addEventListener("mousemove", move, { passive: true });
        document.addEventListener("mouseover", over);
        document.addEventListener("mouseout", out);
        return () => {
            window.removeEventListener("mousemove", move);
            document.removeEventListener("mouseover", over);
            document.removeEventListener("mouseout", out);
        };
    }, []);

    return (
        <>
            <div ref={dotRef} className="cursor-dot" aria-hidden="true"></div>
            <div ref={ringRef} className="cursor-ring" aria-hidden="true"></div>
        </>
    );
}

function Preloader({ onDone }) {
    const ref = useRef(null);
    const [gone, setGone] = useState(false);

    useLayoutEffect(() => {
        if (REDUCED()) {
            onDone();
            setGone(true);
            return;
        }
        const el = ref.current;
        const num = el.querySelector(".pre-num");
        const bar = el.querySelector(".pre-bar");
        const state = { v: 0 };
        const tl = gsap.timeline({ onComplete: () => setGone(true) });
        tl.fromTo(".pre-brand", { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" })
            .to(
                state,
                {
                    v: 100,
                    duration: 1.15,
                    ease: "power2.inOut",
                    onUpdate: () => {
                        num.textContent = String(Math.round(state.v)).padStart(3, "0");
                        gsap.set(bar, { scaleX: state.v / 100 });
                    },
                },
                0.15
            )
            .to(el, { yPercent: -100, duration: 0.8, ease: "power4.inOut", onStart: () => onDone() }, "+=0.15");
        return () => tl.kill();
    }, [onDone]);

    if (gone) return null;

    return (
        <div ref={ref} className="fixed inset-0 z-[100] bg-ink flex flex-col items-center justify-center gap-8">
            <div className="pre-brand flex flex-col items-center gap-5">
                <BrandLogo className="w-12 h-12 rounded-xl" />
                <div className="font-['IBM_Plex_Mono'] text-[10px] tracking-[0.4em] uppercase text-cream/50 text-center px-6">
                    Silicon Valley Consulting Group
                </div>
            </div>
            <div className="w-44 h-px bg-cream/10 overflow-hidden rounded-full">
                <div className="pre-bar h-full w-full origin-left scale-x-0 bg-ember"></div>
            </div>
            <div className="pre-num font-['IBM_Plex_Mono'] text-xs text-cream/40 tracking-[0.3em]">000</div>
        </div>
    );
}

function MagneticButton({ children, className, href, onClick, ...props }) {
    const btnRef = useRef(null);

    useEffect(() => {
        const btn = btnRef.current;
        if (!btn || !window.matchMedia("(pointer: fine)").matches || REDUCED()) return;

        const handleMouseMove = (e) => {
            const rect = btn.getBoundingClientRect();
            const x = (e.clientX - rect.left - rect.width / 2) * 0.2;
            const y = (e.clientY - rect.top - rect.height / 2) * 0.2;
            gsap.to(btn, { x, y, duration: 0.3, ease: "power2.out" });
        };
        const handleMouseLeave = () => {
            gsap.to(btn, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1, 0.3)" });
        };
        btn.addEventListener("mousemove", handleMouseMove);
        btn.addEventListener("mouseleave", handleMouseLeave);
        return () => {
            btn.removeEventListener("mousemove", handleMouseMove);
            btn.removeEventListener("mouseleave", handleMouseLeave);
        };
    }, []);

    const Component = href ? "a" : "button";

    return (
        <Component
            ref={btnRef}
            href={href}
            onClick={onClick}
            className={`relative overflow-hidden group inline-flex items-center justify-center rounded-full transition-transform duration-300 hover:scale-[1.03] cursor-pointer ${className}`}
            {...props}
        >
            <span className="absolute inset-0 bg-black/15 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-in-out z-0 rounded-full"></span>
            <span className="relative z-10 flex items-center gap-2">{children}</span>
        </Component>
    );
}

function TiltCard({ className, children, href, ariaLabel }) {
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (!el || !window.matchMedia("(pointer: fine)").matches || REDUCED()) return;
        gsap.set(el, { transformPerspective: 900 });
        const rx = gsap.quickTo(el, "rotationX", { duration: 0.5, ease: "power3.out" });
        const ry = gsap.quickTo(el, "rotationY", { duration: 0.5, ease: "power3.out" });
        const move = (e) => {
            const r = el.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width - 0.5;
            const py = (e.clientY - r.top) / r.height - 0.5;
            ry(px * 5);
            rx(-py * 5);
            el.style.setProperty("--mx", `${(px + 0.5) * 100}%`);
            el.style.setProperty("--my", `${(py + 0.5) * 100}%`);
        };
        const leave = () => {
            rx(0);
            ry(0);
        };
        el.addEventListener("mousemove", move);
        el.addEventListener("mouseleave", leave);
        return () => {
            el.removeEventListener("mousemove", move);
            el.removeEventListener("mouseleave", leave);
        };
    }, []);

    const Component = href ? "a" : "div";

    return (
        <Component ref={ref} href={href} aria-label={ariaLabel} className={className}>
            {children}
        </Component>
    );
}

function RevealWords({ text, italicWords = [] }) {
    return text.split(" ").map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom pb-[0.14em] -mb-[0.14em]">
            <span
                className={`rw-word inline-block will-change-transform ${
                    italicWords.includes(w) ? "font-['Cormorant_Garamond'] italic font-medium text-ember" : ""
                }`}
            >
                {w}
                {" "}
            </span>
        </span>
    ));
}

function SectionHead({ num, eyebrow, title, italicWords }) {
    const ref = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(
                ".sh-eyebrow",
                { y: 14, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.6, ease: "power3.out", scrollTrigger: { trigger: ref.current, start: "top 85%" } }
            );
            gsap.fromTo(
                ".rw-word",
                { yPercent: 120 },
                { yPercent: 0, duration: 0.9, stagger: 0.05, ease: "power4.out", scrollTrigger: { trigger: ref.current, start: "top 82%" } }
            );
        }, ref);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={ref} className="mb-14 md:mb-20 max-w-3xl">
            <div className="sh-eyebrow flex items-center gap-3 mb-5 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em]">
                <span className="text-ember">{num}</span>
                <span className="h-px w-10 bg-ember/40"></span>
                <span className="text-cream/50">{eyebrow}</span>
            </div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-cream tracking-tight leading-[1.06] font-['Space_Grotesk']">
                <RevealWords text={title} italicWords={italicWords} />
            </h2>
        </div>
    );
}

/* ---------------------------------- navbar ---------------------------------- */

function Navbar({ activeId }) {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    useLayoutEffect(() => {
        if (!menuOpen || REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(".mobile-menu-panel", { opacity: 0 }, { opacity: 1, duration: 0.3, ease: "power2.out" });
            gsap.fromTo(
                ".mobile-menu-link",
                { y: 26, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.5, stagger: 0.06, ease: "power3.out", delay: 0.1 }
            );
        }, menuRef);
        return () => ctx.revert();
    }, [menuOpen]);

    const closeMenu = () => setMenuOpen(false);

    return (
        <>
            <div className="fixed top-4 md:top-6 left-1/2 -translate-x-1/2 w-[94%] max-w-7xl z-[60]">
                <nav
                    className={`flex items-center justify-between px-5 md:px-7 py-3 rounded-full transition-all duration-700 border ${
                        scrolled
                            ? "bg-ink/80 backdrop-blur-xl border-cream/10 shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
                            : "bg-transparent border-transparent"
                    }`}
                >
                    <a
                        href="#top"
                        aria-label="Silicon Valley Consulting Group Home"
                        className="flex items-center gap-3 font-bold tracking-tight text-[11px] md:text-xs uppercase text-cream group"
                    >
                        <BrandLogo className="w-7 h-7 rounded-lg group-hover:scale-110 transition-transform" />
                        <span className="hidden sm:inline font-['Space_Grotesk'] tracking-[0.14em]">SVC Group</span>
                    </a>

                    <div className="hidden lg:flex items-center gap-6 xl:gap-8 text-[10px] xl:text-[11px] font-semibold tracking-[0.22em] uppercase">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                aria-label={l.aria}
                                className={`relative py-1 transition-colors duration-300 ${
                                    activeId === l.id ? "text-cream" : "text-cream/50 hover:text-cream"
                                }`}
                            >
                                {l.label}
                                <span
                                    className={`absolute -bottom-0.5 left-0 h-px bg-ember transition-all duration-400 ${
                                        activeId === l.id ? "w-full" : "w-0"
                                    }`}
                                ></span>
                            </a>
                        ))}
                    </div>

                    <div className="hidden lg:flex items-center gap-3">
                        <ThemeToggle />
                        <MagneticButton
                            href="#contact"
                            aria-label="Contact Silicon Valley Consulting Group"
                            className="bg-ember text-[#0A0B09] px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] flex"
                        >
                            Contact Us
                        </MagneticButton>
                    </div>

                    <div className="flex items-center gap-2 lg:hidden">
                        <ThemeToggle />
                        <button
                            type="button"
                            onClick={() => setMenuOpen(true)}
                            aria-label="Open navigation menu"
                            className="flex items-center justify-center w-10 h-10 -mr-1 rounded-full text-cream cursor-pointer"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                    </div>
                </nav>
            </div>

            {menuOpen && (
                <div ref={menuRef} className="fixed inset-0 z-[80] lg:hidden">
                    <div className="mobile-menu-panel absolute inset-0 bg-ink/[0.97] backdrop-blur-xl flex flex-col">
                        <div className="flex items-center justify-between px-6 py-5">
                            <a
                                href="#top"
                                onClick={closeMenu}
                                aria-label="Silicon Valley Consulting Group Home"
                                className="flex items-center gap-3 font-bold tracking-tight text-xs uppercase text-cream"
                            >
                                <BrandLogo className="w-7 h-7 rounded-lg" />
                                <span className="font-['Space_Grotesk'] tracking-[0.14em]">SVC Group</span>
                            </a>
                            <button
                                type="button"
                                onClick={closeMenu}
                                aria-label="Close navigation menu"
                                className="flex items-center justify-center w-10 h-10 -mr-1 rounded-full text-cream cursor-pointer"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <div className="flex-1 flex flex-col justify-center gap-1 px-8 overflow-y-auto">
                            {NAV_LINKS.map((l, i) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    aria-label={l.aria}
                                    onClick={closeMenu}
                                    className="mobile-menu-link flex items-baseline gap-4 text-3xl sm:text-4xl font-bold text-cream py-3.5 border-b border-cream/10 font-['Space_Grotesk'] tracking-tight"
                                >
                                    <span className="font-['IBM_Plex_Mono'] text-xs text-ember">0{i + 1}</span>
                                    {l.label}
                                </a>
                            ))}
                        </div>
                        <div className="mobile-menu-link px-8 pb-10 pt-4">
                            <MagneticButton
                                href="#contact"
                                onClick={closeMenu}
                                aria-label="Contact Silicon Valley Consulting Group"
                                className="bg-ember text-[#0A0B09] px-6 py-4 text-sm font-bold uppercase tracking-widest flex items-center justify-center w-full"
                            >
                                Contact Us
                            </MagneticButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

/* ----------------------------------- hero ----------------------------------- */

function NeuralCanvas() {
    const canvasRef = useRef(null);
    const { theme } = useTheme();

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let raf = 0;
        let w = 0;
        let h = 0;
        let particles = [];
        const reduced = REDUCED();
        const dotColor = theme === "light" ? "rgba(23,24,18,0.35)" : "rgba(242,240,233,0.45)";
        const linkColor = theme === "light" ? "58,92,68" : "163,194,168";

        const build = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = canvas.clientWidth;
            h = canvas.clientHeight;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const count = Math.min(90, Math.floor((w * h) / 24000));
            particles = Array.from({ length: count }, () => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * 0.24,
                vy: (Math.random() - 0.5) * 0.24,
                r: Math.random() * 1.3 + 0.5,
            }));
        };

        const draw = (advance) => {
            ctx.clearRect(0, 0, w, h);
            const linkDist = 130;
            for (const p of particles) {
                if (advance) {
                    p.x += p.vx;
                    p.y += p.vy;
                    if (p.x < -10) p.x = w + 10;
                    if (p.x > w + 10) p.x = -10;
                    if (p.y < -10) p.y = h + 10;
                    if (p.y > h + 10) p.y = -10;
                }
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = dotColor;
                ctx.fill();
            }
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const a = particles[i];
                    const b = particles[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const d = Math.hypot(dx, dy);
                    if (d < linkDist) {
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.strokeStyle = `rgba(${linkColor},${(1 - d / linkDist) * 0.14})`;
                        ctx.lineWidth = 1;
                        ctx.stroke();
                    }
                }
            }
        };

        const loop = () => {
            draw(true);
            raf = requestAnimationFrame(loop);
        };

        build();
        if (reduced) {
            draw(false);
        } else {
            raf = requestAnimationFrame(loop);
        }
        const onResize = () => {
            build();
            if (reduced) draw(false);
        };
        window.addEventListener("resize", onResize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
        };
    }, [theme]);

    return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" aria-hidden="true" />;
}

function HeroBackdrop() {
    const ref = useRef(null);
    const { theme } = useTheme();
    const isLight = theme === "light";
    const [videoOk, setVideoOk] = useState(true);
    const [videoReady, setVideoReady] = useState(false);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.to(".hero-glow-a", { xPercent: 14, yPercent: 10, duration: 16, yoyo: true, repeat: -1, ease: "sine.inOut" });
            gsap.to(".hero-glow-b", { xPercent: -12, yPercent: -14, duration: 20, yoyo: true, repeat: -1, ease: "sine.inOut" });
        }, ref);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={ref} className="absolute inset-0 z-0 overflow-hidden" aria-hidden="true">
            <div className={`hero-glow-a absolute -top-48 -left-48 w-[48rem] h-[48rem] rounded-full blur-[140px] ${isLight ? "bg-moss/10" : "bg-moss/50"}`}></div>
            <div className={`hero-glow-b absolute top-1/3 -right-56 w-[42rem] h-[42rem] rounded-full blur-[160px] ${isLight ? "bg-ember/10" : "bg-ember/15"}`}></div>
            <div className={`absolute inset-0 ${DEMO_GRID} bg-[size:56px_56px] opacity-60 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]`}></div>
            <NeuralCanvas />
            {videoOk && !isLight && (
                <video
                    className={`absolute inset-0 w-full h-full object-cover mix-blend-screen transition-opacity duration-[1600ms] ${
                        videoReady ? "opacity-40" : "opacity-0"
                    }`}
                    src={HERO_VIDEO_SRC}
                    autoPlay
                    muted
                    loop
                    playsInline
                    onError={() => setVideoOk(false)}
                    onCanPlay={() => setVideoReady(true)}
                />
            )}
            <div className="hero-vignette absolute inset-0"></div>
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink to-transparent"></div>
        </div>
    );
}

const HERO_STATS = [
    { value: 9, suffix: "", label: "Agents in production" },
    { value: 6, suffix: "", label: "Industries served" },
    { value: 100, suffix: "%", label: "High-stakes actions human-gated" },
    { value: 24, suffix: "h", label: "Max. inquiry response" },
];

function Hero({ booted }) {
    const heroRef = useRef(null);

    useLayoutEffect(() => {
        if (!booted || REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".hero-count").forEach((el) => {
                el.textContent = "0" + (el.dataset.suffix || "");
            });
            const tl = gsap.timeline();
            tl.fromTo(".hero-line", { yPercent: 115 }, { yPercent: 0, duration: 1.1, stagger: 0.1, ease: "power4.out" }, 0.05)
                .fromTo(".hero-fade", { y: 26, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, stagger: 0.1, ease: "power3.out" }, 0.5)
                .fromTo(".hero-stat", { y: 18, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, stagger: 0.08, ease: "power3.out" }, 0.9);

            gsap.utils.toArray(".hero-count").forEach((el) => {
                const target = parseFloat(el.dataset.value);
                const obj = { v: 0 };
                gsap.to(obj, {
                    v: target,
                    duration: 1.8,
                    delay: 1.0,
                    ease: "power2.out",
                    onUpdate: () => {
                        el.textContent = Math.round(obj.v) + (el.dataset.suffix || "");
                    },
                });
            });

            gsap.to(".hero-scroll-dot", { y: 26, opacity: 0, duration: 1.6, repeat: -1, ease: "power2.in", delay: 1.5 });

            gsap.to(".hero-content", {
                yPercent: -10,
                opacity: 0.2,
                ease: "none",
                scrollTrigger: { trigger: heroRef.current, start: "top top", end: "bottom top", scrub: true },
            });
        }, heroRef);
        return () => ctx.revert();
    }, [booted]);

    return (
        <section ref={heroRef} id="top" className="relative min-h-[100dvh] w-full flex flex-col justify-center overflow-hidden">
            <HeroBackdrop />

            <div className="hero-content relative z-10 w-full max-w-7xl mx-auto px-6 pt-32 pb-16 md:pt-36">
                <div className="hero-fade flex items-center gap-3 mb-7 font-['IBM_Plex_Mono'] text-[10px] md:text-[11px] uppercase tracking-[0.32em] text-cream/60">
                    <span className="w-1.5 h-1.5 rounded-full bg-ember animate-pulse"></span>
                    Enterprise AI Agents · AI Infrastructure · Physical AI
                </div>

                <h1 className="font-['Space_Grotesk'] font-bold text-cream tracking-tight leading-[0.98] text-[2.7rem] sm:text-6xl md:text-7xl xl:text-[6.5rem] mb-8">
                    <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
                        <span className="hero-line block will-change-transform">AI agents built for</span>
                    </span>
                    <span className="block overflow-hidden pb-[0.18em] -mb-[0.1em]">
                        <span className="hero-line block will-change-transform font-['Cormorant_Garamond'] italic font-medium text-ember">
                            enterprise operations.
                        </span>
                    </span>
                </h1>

                <p className="hero-fade text-cream/65 text-base md:text-xl font-light max-w-xl leading-relaxed font-['Outfit'] mb-10">
                    We build AI agents that put your company knowledge to work, connect to the tools you already run, and take on
                    repeatable work across operations, support, finance, and logistics, backed by AI infrastructure and physical AI systems.
                </p>

                <div className="hero-fade flex flex-wrap items-center gap-4 md:gap-6">
                    <MagneticButton
                        href="#services"
                        aria-label="Explore AI Capabilities and Robotics Services"
                        className="bg-ember text-[#0A0B09] px-8 py-4 md:px-9 md:py-5 text-xs md:text-sm font-bold uppercase tracking-[0.18em] flex items-center gap-2"
                    >
                        See what we build <ArrowRight className="w-4 h-4" />
                    </MagneticButton>
                    <a
                        href="#contact"
                        aria-label="Contact Silicon Valley Consulting Group"
                        className="group inline-flex items-center gap-2 text-cream/80 hover:text-cream text-xs md:text-sm font-bold uppercase tracking-[0.18em] border border-cream/20 hover:border-cream/50 rounded-full px-8 py-4 transition-colors cursor-pointer"
                    >
                        Talk to us
                        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                </div>

                <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 border-t border-cream/10 pt-8 max-w-4xl">
                    {HERO_STATS.map((s, i) => (
                        <div key={i} className="hero-stat">
                            <div
                                className="hero-count font-['IBM_Plex_Mono'] text-2xl md:text-4xl font-bold text-sage mb-1.5"
                                data-value={s.value}
                                data-suffix={s.suffix}
                            >
                                {s.value}
                                {s.suffix}
                            </div>
                            <div className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-cream/45 font-semibold leading-relaxed">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="hero-fade absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
                <span className="font-['IBM_Plex_Mono'] text-[9px] tracking-[0.4em] uppercase text-cream/35">Scroll</span>
                <div className="w-px h-10 bg-cream/15 relative overflow-hidden">
                    <div className="hero-scroll-dot absolute top-0 left-0 w-px h-3 bg-ember"></div>
                </div>
            </div>
        </section>
    );
}

/* ---------------------------------- marquee ---------------------------------- */

const ORGS = ["NASA", "NVIDIA", "MERCEDES-BENZ", "BIG 4 CONSULTING", "SV AI & ROBOTICS STARTUPS", "ENTERPRISE SAAS"];

function Marquee() {
    return (
        <section aria-label="Team backgrounds" className="relative border-y border-cream/10 bg-ink py-7 md:py-9 overflow-hidden">
            <div className="text-center mb-5 font-['IBM_Plex_Mono'] text-[9px] md:text-[10px] tracking-[0.4em] uppercase text-cream/30">
                Built by operators from
            </div>
            <div className="relative">
                <div className="absolute left-0 inset-y-0 w-20 md:w-40 bg-gradient-to-r from-ink to-transparent z-10 pointer-events-none"></div>
                <div className="absolute right-0 inset-y-0 w-20 md:w-40 bg-gradient-to-l from-ink to-transparent z-10 pointer-events-none"></div>
                <div className="flex w-max marquee-track">
                    {[0, 1].map((dup) => (
                        <div key={dup} aria-hidden={dup === 1} className="flex items-center shrink-0">
                            {ORGS.map((o, i) => (
                                <span key={i} className="flex items-center gap-8 md:gap-14 pr-8 md:pr-14">
                                    <span className="font-['Space_Grotesk'] text-lg md:text-2xl font-medium tracking-[0.16em] text-cream/35 whitespace-nowrap">
                                        {o}
                                    </span>
                                    <span className="w-1.5 h-1.5 rotate-45 bg-ember/60 shrink-0"></span>
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ------------------------------ service demo cards ------------------------------ */

function ShufflerCard() {
    const [cards, setCards] = useState([
        { id: 1, label: "Executive Alignment", status: "Complete" },
        { id: 2, label: "Opportunity Mapping", status: "In Progress" },
        { id: 3, label: "Requirements", status: "Pending" },
    ]);

    useEffect(() => {
        if (REDUCED()) return;
        const interval = setInterval(() => {
            setCards((prev) => {
                const next = [...prev];
                const last = next.pop();
                next.unshift(last);
                return next;
            });
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative h-44 w-full mt-10">
            {cards.map((c, i) => (
                <div
                    key={c.id}
                    className="absolute inset-x-0 bg-well border border-cream/10 rounded-2xl p-5 transition-all duration-700 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
                    style={{ top: `${i * 14}px`, scale: 1 - i * 0.04, zIndex: 10 - i, opacity: 1 - i * 0.2 }}
                >
                    <div className="flex justify-between items-center text-xs">
                        <span className="font-bold text-cream/90 tracking-wide">{c.label}</span>
                        <span
                            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest ${
                                i === 0 ? "bg-sage text-ink" : "bg-cream/5 text-cream/35"
                            }`}
                        >
                            {c.status}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function TypewriterCard() {
    const [text, setText] = useState("");
    const fullText = "> scheduler online...\n> gpu fabric healthy...\n> model gateway ready...\n> policy controls active...\n> system ready.";

    useEffect(() => {
        if (REDUCED()) {
            setText(fullText);
            return;
        }
        let i = 0;
        const interval = setInterval(() => {
            setText(fullText.slice(0, i));
            i++;
            if (i > fullText.length + 20) i = 0;
        }, 50);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="font-['IBM_Plex_Mono'] bg-well border border-cream/10 text-sage p-5 rounded-2xl mt-10 text-[10px] md:text-xs h-[172px] overflow-hidden whitespace-pre-wrap leading-relaxed flex flex-col">
            <div className="flex items-center justify-between mb-4 border-b border-cream/5 pb-3">
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-ember animate-pulse"></div>
                    <span className="text-cream/35 uppercase tracking-widest text-[9px] font-bold">Live Telemetry</span>
                </div>
            </div>
            <div>
                {text}
                <span className="inline-block w-1.5 h-3 bg-ember ml-1 align-middle animate-pulse"></span>
            </div>
        </div>
    );
}

function AgentCard() {
    const rootRef = useRef(null);
    const dotRef = useRef(null);
    const line1 = useRef(null);
    const line2 = useRef(null);
    const line3 = useRef(null);
    const D = useDemoColors();

    useEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            tl.to(dotRef.current, { cx: 100, cy: 80, duration: 1.5, ease: "power1.inOut" })
                .to(line1.current, { strokeDashoffset: 0, duration: 0.2 }, "-=0.2")
                .to(dotRef.current, { cx: 180, cy: 20, duration: 1.5, ease: "power1.inOut" })
                .to(line2.current, { strokeDashoffset: 0, duration: 0.2 }, "-=0.2")
                .to(dotRef.current, { cx: 20, cy: 20, duration: 1.5, ease: "power1.inOut" })
                .to(line3.current, { strokeDashoffset: 0, duration: 0.2 }, "-=0.2");
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={rootRef} className="relative mt-10 w-full h-44 bg-cream/[0.03] rounded-2xl flex items-center justify-center border border-cream/10 overflow-hidden">
            <svg viewBox="0 0 200 100" className="w-full h-full relative z-0">
                <line ref={line1} x1="20" y1="20" x2="100" y2="80" stroke={D.sage} strokeOpacity="0.4" strokeWidth="2" strokeDasharray="100" strokeDashoffset="100" />
                <line ref={line2} x1="100" y1="80" x2="180" y2="20" stroke={D.sage} strokeOpacity="0.4" strokeWidth="2" strokeDasharray="100" strokeDashoffset="100" />
                <line ref={line3} x1="180" y1="20" x2="20" y2="20" stroke={D.sage} strokeOpacity="0.4" strokeWidth="2" strokeDasharray="160" strokeDashoffset="160" />
                <circle cx="20" cy="20" r="6" fill="none" stroke={D.sage} strokeWidth="2" />
                <circle cx="100" cy="80" r="6" fill="none" stroke={D.sage} strokeWidth="2" />
                <circle cx="180" cy="20" r="6" fill="none" stroke={D.sage} strokeWidth="2" />
            </svg>
            <svg viewBox="0 0 200 100" className="absolute inset-0 w-full h-full z-10">
                <circle ref={dotRef} cx="20" cy="20" r="5" fill={D.ember} />
            </svg>
        </div>
    );
}

function CompanyBrainCard() {
    const rootRef = useRef(null);
    const coreRef = useRef(null);
    const D = useDemoColors();

    useEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.set(".brain-source", { opacity: 0.45 });
            gsap.set(".brain-link", { strokeDashoffset: 110 });
            gsap.set(".brain-status", { opacity: 0, y: 4 });
            const tl = gsap.timeline({ repeat: -1, repeatDelay: 1 });
            tl.to(".brain-source", { opacity: 1, duration: 0.35, stagger: 0.22 })
                .to(".brain-link", { strokeDashoffset: 0, duration: 0.55, stagger: 0.14, ease: "power1.inOut" }, "-=0.65")
                .to(coreRef.current, { scale: 1.08, duration: 0.35, yoyo: true, repeat: 1, ease: "power2.inOut" })
                .to(".brain-status", { opacity: 1, y: 0, duration: 0.3 }, "-=0.2")
                .to({}, { duration: 1.5 })
                .to(".brain-status", { opacity: 0, y: 4, duration: 0.25 })
                .to(".brain-link", { strokeDashoffset: 110, duration: 0.35 })
                .to(".brain-source", { opacity: 0.45, duration: 0.3 }, "<");
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={rootRef} className="relative mt-10 w-full h-44 bg-cream/[0.03] border border-cream/10 rounded-2xl overflow-hidden">
            <svg viewBox="0 0 300 176" className="absolute inset-0 w-full h-full" aria-hidden="true">
                {[
                    [58, 38, 150, 88],
                    [242, 38, 150, 88],
                    [58, 138, 150, 88],
                    [242, 138, 150, 88],
                ].map(([x1, y1, x2, y2], index) => (
                    <line
                        key={index}
                        className="brain-link"
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={D.sage}
                        strokeOpacity="0.45"
                        strokeWidth="1.5"
                        strokeDasharray="110"
                    />
                ))}
                <circle cx="150" cy="88" r="48" fill="none" stroke={D.ember} strokeOpacity="0.18" />
                <circle cx="150" cy="88" r="40" fill={D.emberTint(0.06)} stroke={D.ember} strokeOpacity="0.45" />
            </svg>

            <span className="brain-source absolute left-4 top-4 rounded-full border border-cream/10 bg-well px-2.5 py-1 font-['IBM_Plex_Mono'] text-[8px] uppercase tracking-wider text-cream/55">Knowledge</span>
            <span className="brain-source absolute right-4 top-4 rounded-full border border-cream/10 bg-well px-2.5 py-1 font-['IBM_Plex_Mono'] text-[8px] uppercase tracking-wider text-cream/55">Data</span>
            <span className="brain-source absolute left-4 bottom-4 rounded-full border border-cream/10 bg-well px-2.5 py-1 font-['IBM_Plex_Mono'] text-[8px] uppercase tracking-wider text-cream/55">Decisions</span>
            <span className="brain-source absolute right-4 bottom-4 rounded-full border border-cream/10 bg-well px-2.5 py-1 font-['IBM_Plex_Mono'] text-[8px] uppercase tracking-wider text-cream/55">Tools</span>

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div ref={coreRef} className="flex h-20 w-20 flex-col items-center justify-center rounded-full text-center">
                    <Brain className="mb-1 h-5 w-5 text-ember" aria-hidden="true" />
                    <span className="font-['IBM_Plex_Mono'] text-[8px] font-bold uppercase tracking-wider text-cream/80">Company<br />context</span>
                    <span className="brain-status mt-1 text-[6px] font-bold uppercase tracking-widest text-sage">Ready</span>
                </div>
            </div>
        </div>
    );
}

function SchedulerCard() {
    const spaceRef = useRef(null);
    const D = useDemoColors();

    useEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            gsap.to(".drone-body", { y: -5, duration: 1.5, yoyo: true, repeat: -1, ease: "sine.inOut" });
            tl.to(".drone-group", { x: 50, y: 15, duration: 2, ease: "power1.inOut" })
                .to(".drone-scan", { scaleY: 1.5, duration: 0.5, yoyo: true, repeat: 1 })
                .to(".drone-group", { x: -30, y: 40, duration: 2.5, ease: "power1.inOut" })
                .to(".drone-scan", { scaleY: 1.5, duration: 0.5, yoyo: true, repeat: 1 })
                .to(".drone-group", { x: 0, y: 0, duration: 2, ease: "power1.inOut" });
        }, spaceRef);
        return () => ctx.revert();
    }, []);

    return (
        <div className="relative mt-10 w-full h-44 bg-cream/[0.02] rounded-2xl flex items-center justify-center border border-cream/10 overflow-hidden font-['IBM_Plex_Mono']">
            <div className="absolute top-3 left-4 text-[9px] uppercase tracking-widest text-cream/35 font-bold z-20 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-ember rounded-full animate-pulse"></div> Mapping Environment
            </div>

            <div ref={spaceRef} className="relative w-[90%] h-[70%] mt-4 overflow-hidden rounded-xl border border-cream/5 bg-well flex items-center justify-center">
                <div className={`absolute inset-0 ${DEMO_GRID} bg-[size:10px_10px]`} style={{ transform: "rotateX(45deg) scale(1.5)" }}></div>

                <svg viewBox="0 0 200 100" className="w-full h-full relative z-10 overflow-visible">
                    <g className="drone-group" transform="translate(100, 40)">
                        <polygon className="drone-scan" points="0,5 -30,40 30,40" fill="rgba(232,102,60,0.12)" style={{ transformOrigin: "top center" }} />
                        <line className="drone-scan" x1="0" y1="5" x2="-30" y2="40" stroke="rgba(232,102,60,0.3)" strokeWidth="1" strokeDasharray="2 2" />
                        <line className="drone-scan" x1="0" y1="5" x2="30" y2="40" stroke="rgba(232,102,60,0.3)" strokeWidth="1" strokeDasharray="2 2" />

                        <g className="drone-body">
                            <rect x="-10" y="-2" width="20" height="4" rx="2" fill={D.cream} />
                            <circle cx="-10" cy="0" r="3" fill={D.ink} stroke={D.cream} strokeWidth="1.5" />
                            <circle cx="10" cy="0" r="3" fill={D.ink} stroke={D.cream} strokeWidth="1.5" />
                            <circle cx="-10" cy="0" r="4" fill="none" stroke={D.sage} strokeWidth="1" className="animate-[spin_0.5s_linear_infinite]" strokeDasharray="4 2" />
                            <circle cx="10" cy="0" r="4" fill="none" stroke={D.sage} strokeWidth="1" className="animate-[spin_0.5s_linear_infinite]" strokeDasharray="4 2" />
                            <circle cx="0" cy="3" r="1.5" fill={D.ember} />
                        </g>
                    </g>

                    <circle cx="150" cy="55" r="2" fill={D.sage} />
                    <circle cx="70" cy="80" r="2" fill={D.sage} />
                    <circle cx="100" cy="40" r="2" fill={D.sage} />
                </svg>
            </div>
        </div>
    );
}

function VideoSearchCard() {
    const boxRef = useRef(null);
    const D = useDemoColors();

    useEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            tl.to(".vid-person", { x: 140, duration: 6, ease: "linear" });
            gsap.to(".vid-bbox", { x: 140, duration: 6, ease: "linear", repeat: -1 });
            gsap.to(".vid-bbox", { borderColor: D.ember, duration: 0.2, repeat: 3, yoyo: true, delay: 2, repeatDelay: 6 });
        }, boxRef);
        return () => ctx.revert();
    }, [D]);

    return (
        <div ref={boxRef} className="relative mt-10 w-full h-44 flex flex-col justify-end bg-cream/[0.02] rounded-2xl border border-cream/10 overflow-hidden p-4 font-['IBM_Plex_Mono']">
            <div className="absolute top-3 left-4 text-[9px] uppercase tracking-widest font-bold text-cream/35 flex items-center gap-1.5 z-20">
                <div className="w-1.5 h-1.5 bg-ember rounded-full animate-pulse"></div> Live Feed Analyzed
            </div>

            <div className="relative w-full h-28 bg-well border border-cream/5 rounded-lg overflow-hidden flex items-center mt-auto">
                <div className={`absolute inset-0 ${DEMO_GRID} bg-[size:20px_20px]`}></div>

                <svg viewBox="0 0 200 100" className="w-full h-full relative z-10">
                    <circle className="vid-person" cx="30" cy="40" r="5" fill={D.sage} />
                    <rect className="vid-person" x="25" y="47" width="10" height="15" rx="2" fill={D.sage} />
                    <line className="vid-person" x1="27" y1="62" x2="25" y2="75" stroke={D.sage} strokeWidth="2.5" strokeLinecap="round" />
                    <line className="vid-person" x1="33" y1="62" x2="35" y2="75" stroke={D.sage} strokeWidth="2.5" strokeLinecap="round" />
                </svg>

                <div className="vid-bbox absolute left-4 top-5 w-10 h-[60px] border-2 border-sage bg-sage/5 rounded-sm z-20">
                    <div className="vid-label absolute -top-4 -left-0.5 bg-sage text-ink text-[6px] font-bold px-1 py-0.5 rounded whitespace-nowrap">
                        PERSON: 99%
                    </div>
                </div>
            </div>
        </div>
    );
}

const SERVICES = [
    {
        icon: Network,
        title: "AI Agents & Automation",
        desc: "Task-anchored agents executing workflows across ERP, CRM, and data systems.",
        proof: "Live: agents that extract documents, route exceptions for approval, and cascade work across systems of record.",
        href: "/services/ai-agents-automation",
        Demo: AgentCard,
    },
    {
        icon: Brain,
        title: "Company Brain & Agent Context",
        desc: "Shared company knowledge, business meaning, memory, permissions, and tools for every agent.",
        proof: "A governed context layer connecting documents, data, decisions, workflows, and systems of action.",
        href: "/services/company-brain-enterprise-context",
        Demo: CompanyBrainCard,
    },
    {
        icon: Cpu,
        title: "AI Infrastructure & AI Factories",
        desc: "Compute, networking, storage, model serving, and operations for production AI.",
        proof: "Built for real workloads: governed clusters, observable inference, tenant isolation, and capacity discipline.",
        href: "/services/ai-infrastructure-ai-factories",
        Demo: TypewriterCard,
    },
    {
        icon: Activity,
        title: "AI & Data Platforms",
        desc: "Data foundations, retrieval systems, pipelines, and governance for enterprise AI.",
        proof: "Designed to connect trusted enterprise data to agents, analytics, company context, and model workflows.",
        href: "/services/ai-data-platforms",
        Demo: ShufflerCard,
    },
    {
        icon: Bot,
        title: "Physical AI",
        desc: "Perception, manipulation, and mobile robotics for logistics and manufacturing.",
        proof: "Live: a hands-free field operations agent whose tool calls write structured data into the system of record.",
        href: "/services/physical-ai-robotics",
        Demo: SchedulerCard,
    },
    {
        icon: Video,
        title: "Video Search & Insights",
        desc: "Advanced video analytics to extract actionable insights from visual content.",
        proof: "Live: walkthrough-video analysis producing measurements, condition assessments, and defect flags.",
        href: "/services/video-search-analytics",
        Demo: VideoSearchCard,
    },
];

function Services() {
    const rootRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".svc-card").forEach((card, i) => {
                gsap.fromTo(
                    card,
                    { y: 60, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: 1,
                        delay: (i % 3) * 0.12,
                        ease: "power3.out",
                        scrollTrigger: { trigger: card, start: "top 88%" },
                    }
                );
            });
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <section ref={rootRef} id="services" className="relative py-24 md:py-36 px-6 max-w-7xl mx-auto">
            <SectionHead num="01" eyebrow="Service Offerings" title="Advisory plus delivery." italicWords={["delivery."]} />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {SERVICES.map((s, i) => (
                    <TiltCard
                        key={i}
                        href={s.href}
                        ariaLabel={`Explore ${s.title}`}
                        className="svc-card card-glow group bg-onyx border border-cream/10 rounded-[2rem] p-8 flex flex-col hover:border-cream/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember focus-visible:ring-offset-4 focus-visible:ring-offset-ink transition-colors duration-500 cursor-pointer"
                    >
                        <div className="flex items-start justify-between mb-6">
                            <s.icon className="w-6 h-6 text-sage" aria-hidden="true" />
                            <span className="flex items-center justify-center w-9 h-9 rounded-full border border-cream/15 text-cream/60 group-hover:text-ember group-hover:border-ember/45 transition-colors duration-300" aria-hidden="true">
                                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </span>
                        </div>
                        <h3 className="text-xl font-bold text-cream mb-2 font-['Space_Grotesk']">
                            <span className="text-cream group-hover:text-ember transition-colors">{s.title}</span>
                        </h3>
                        <p className="text-cream/55 text-sm font-light leading-relaxed">{s.desc}</p>
                        <p className="mt-3 flex items-start gap-2 text-cream/45 text-[11px] leading-relaxed font-['IBM_Plex_Mono']">
                            <span className="mt-1 w-1.5 h-1.5 shrink-0 rounded-full bg-ember animate-pulse"></span>
                            <span>{s.proof}</span>
                        </p>
                        <div className="mt-auto">
                            <s.Demo />
                            <span className="mt-5 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-sage group-hover:text-ember transition-colors font-['IBM_Plex_Mono'] font-bold">
                                Explore capability
                                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                            </span>
                        </div>
                    </TiltCard>
                ))}
            </div>
        </section>
    );
}

/* ------------------------------ industry demo cards ------------------------------ */

function ManufacturingCard() {
    const boxRef = useRef(null);
    const D = useDemoColors();

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            tl.to(".agv-grp", { x: 100, y: 0, duration: 2, ease: "power1.inOut" })
                .to(".agv-grp", { x: 100, y: 40, duration: 1, ease: "power1.inOut" })
                .to(".agv-grp", { x: 0, y: 40, duration: 2, ease: "power1.inOut" })
                .to(".agv-grp", { x: 0, y: 0, duration: 1, ease: "power1.inOut" });
            gsap.to(".agv-scan", { scale: 1.5, opacity: 0, duration: 1, repeat: -1, transformOrigin: "center" });
        }, boxRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={boxRef} className="relative mt-8 w-full h-44 flex flex-col justify-between overflow-hidden rounded-2xl border border-cream/10 bg-cream/[0.02] p-4 font-['IBM_Plex_Mono']">
            <div className="flex justify-between items-start z-10 w-full mb-2">
                <span className="text-[9px] uppercase tracking-widest text-cream/45 font-bold border border-cream/10 rounded-full px-2 py-0.5">Fleet Orchestration</span>
                <span className="text-sm font-bold text-sage">Optimal</span>
            </div>

            <div className="relative flex-1 w-full flex items-center justify-center border border-cream/5 rounded-xl overflow-hidden bg-well">
                <div className={`absolute inset-0 ${DEMO_GRID} bg-[size:16px_16px]`}></div>

                <svg viewBox="0 0 160 80" className="w-full h-full relative z-10 overflow-visible">
                    <path d="M 30 20 L 130 20 L 130 60 L 30 60 Z" fill="none" stroke={D.tint(0.18)} strokeWidth="1.5" strokeDasharray="4 4" />
                    <rect x="25" y="15" width="10" height="10" rx="2" fill={D.tint(0.06)} stroke={D.tint(0.2)} strokeWidth="1" />
                    <rect x="125" y="15" width="10" height="10" rx="2" fill={D.tint(0.06)} stroke={D.tint(0.2)} strokeWidth="1" />
                    <rect x="125" y="55" width="10" height="10" rx="2" fill={D.tint(0.06)} stroke={D.tint(0.2)} strokeWidth="1" />
                    <rect x="25" y="55" width="10" height="10" rx="2" fill={D.tint(0.06)} stroke={D.tint(0.2)} strokeWidth="1" />

                    <g className="agv-grp" transform="translate(0, 0)">
                        <circle cx="30" cy="20" r="8" fill="none" stroke={D.sage} strokeWidth="1" opacity="0.5" className="agv-scan" />
                        <rect x="27" y="17" width="6" height="6" rx="1.5" fill={D.sage} />
                        <circle cx="30" cy="20" r="1.5" fill={D.ink} />
                        <text x="30" y="10" fontSize="6" fill={D.cream} fontWeight="bold" textAnchor="middle" opacity="0.6">
                            AGV-04
                        </text>
                    </g>
                </svg>
            </div>
        </div>
    );
}

function EnergyCard() {
    const chartRef = useRef(null);
    const valRef = useRef(null);
    const D = useDemoColors();

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(".energy-path", { strokeDashoffset: 300 }, { strokeDashoffset: 0, duration: 4, ease: "none", repeat: -1 });
        }, chartRef);
        const interval = setInterval(() => {
            if (valRef.current) valRef.current.innerText = (400 + Math.random() * 50).toFixed(1) + " MW";
        }, 500);
        return () => {
            ctx.revert();
            clearInterval(interval);
        };
    }, []);

    return (
        <div ref={chartRef} className="relative mt-8 w-full h-44 flex flex-col justify-between overflow-hidden rounded-2xl border border-cream/10 bg-cream/[0.02] p-4 font-['IBM_Plex_Mono']">
            <div className="flex justify-between items-start z-10">
                <span className="text-[9px] uppercase tracking-widest text-cream/45 font-bold border border-cream/10 rounded-full px-2 py-0.5">Grid Load</span>
                <span ref={valRef} className="text-sm font-bold text-ember">
                    423.5 MW
                </span>
            </div>
            <div className="relative h-20 w-full mt-auto border-b border-l border-cream/10 pb-2 pl-2">
                <div className={`absolute inset-0 ${DEMO_GRID} bg-[size:10px_10px]`}></div>
                <svg viewBox="0 0 200 80" className="absolute bottom-0 w-full h-full overflow-visible" preserveAspectRatio="none">
                    <path
                        className="energy-path"
                        d="M0,70 Q20,60 40,70 T80,40 T120,50 T160,20 T200,30"
                        fill="none"
                        stroke={D.ember}
                        strokeWidth="2"
                        strokeDasharray="300"
                        strokeDashoffset="300"
                        strokeLinecap="round"
                    />
                    <path d="M200,30 Q220,40 250,10" fill="none" stroke={D.ember} strokeOpacity="0.3" strokeWidth="2" strokeDasharray="4 4" />
                </svg>
            </div>
        </div>
    );
}

function PublicSectorCard() {
    const mapRef = useRef(null);
    const D = useDemoColors();

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            tl.to(".city-marker", { y: -8, opacity: 1, duration: 0.6, ease: "back.out(2)" })
                .to(".city-bldg", { fill: D.emberTint(0.15), stroke: D.ember, duration: 0.3 }, "-=0.4")
                .fromTo(".city-ring", { scale: 0.8, opacity: 1 }, { scale: 3, opacity: 0, duration: 1.5, ease: "power1.out" }, "-=0.3")
                .to({}, { duration: 1 })
                .to(".city-marker", { y: 0, opacity: 0, duration: 0.3, ease: "power1.in" })
                .to(".city-bldg", { fill: D.tint(0.06), stroke: D.tint(0.15), duration: 0.3 }, "-=0.3");
        }, mapRef);
        return () => ctx.revert();
    }, [D]);

    const bldg = { fill: D.tint(0.06), stroke: D.tint(0.15), strokeWidth: 1, rx: 1 };

    return (
        <div ref={mapRef} className="relative mt-8 w-full h-44 flex flex-col justify-between overflow-hidden rounded-2xl border border-cream/10 bg-cream/[0.02] p-4 font-['IBM_Plex_Mono']">
            <div className="flex justify-between items-start z-10 w-full mb-2">
                <span className="text-[9px] uppercase tracking-widest text-cream/45 font-bold border border-cream/10 rounded-full px-2 py-0.5">Urban Intel</span>
                <span className="text-sm font-bold text-ember flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-ember rounded-full animate-pulse"></span> Alert
                </span>
            </div>

            <div className="relative flex-1 w-full rounded-xl border border-cream/5 overflow-hidden bg-well flex items-center justify-center">
                <svg viewBox="0 0 200 100" className="w-full h-full absolute inset-0">
                    <path d="M 0 35 L 200 35 M 0 65 L 200 65 M 70 0 L 70 100 M 130 0 L 130 100" stroke={D.tint(0.07)} strokeWidth="8" fill="none" />
                    <rect x="15" y="10" width="40" height="15" {...bldg} />
                    <rect x="85" y="10" width="30" height="15" className="city-bldg" {...bldg} />
                    <rect x="145" y="10" width="40" height="15" {...bldg} />
                    <rect x="15" y="45" width="40" height="10" {...bldg} />
                    <rect x="85" y="45" width="30" height="10" {...bldg} />
                    <rect x="145" y="45" width="40" height="10" {...bldg} />
                    <rect x="15" y="75" width="40" height="15" {...bldg} />
                    <rect x="85" y="75" width="30" height="15" {...bldg} />
                    <rect x="145" y="75" width="40" height="15" {...bldg} />

                    <g transform="translate(100, 17)">
                        <circle className="city-ring" cx="0" cy="0" r="5" fill="none" stroke={D.ember} strokeWidth="1" opacity="0" style={{ transformOrigin: "center" }} />
                        <g className="city-marker" opacity="0">
                            <path d="M0,0 C-3,-4 -4,-7 0,-11 C4,-7 3,-4 0,0 Z" fill={D.ember} />
                            <circle cx="0" cy="-7.5" r="1.5" fill={D.ink} />
                        </g>
                    </g>
                </svg>
            </div>
        </div>
    );
}

function FinanceCard() {
    const kycRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            gsap.utils.toArray(".kyc-line").forEach((line, i) => {
                tl.to(".kyc-scanner", { y: i * 14 + 12, duration: 0.4, ease: "power2.inOut" })
                    .to(line.querySelector(".kyc-fill"), { width: "100%", duration: 0.3, ease: "none" })
                    .to(line.querySelector(".kyc-status"), { opacity: 1, duration: 0.1 });
            });
            tl.to(".kyc-approved", { opacity: 1, scale: 1, duration: 0.3, ease: "back.out" })
                .to({}, { duration: 1.5 })
                .to(".kyc-scanner", { opacity: 0, duration: 0.2 })
                .to(".kyc-reset", { width: "0%", opacity: 0, duration: 0.1 })
                .set(".kyc-scanner", { y: 0, opacity: 1 });
        }, kycRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={kycRef} className="relative mt-8 w-full h-44 flex justify-center items-center overflow-hidden rounded-2xl border border-cream/10 bg-cream/[0.02] p-4 font-['IBM_Plex_Mono']">
            <div className="absolute top-3 left-4 text-[9px] uppercase tracking-widest text-cream/35 font-bold z-20">Doc AI KYC</div>

            <div className="relative w-44 h-28 bg-well border border-cream/10 rounded p-4 flex flex-col gap-2 mt-4 z-10">
                <div className="absolute left-0 right-0 h-4 bg-sage/10 border-y border-sage/30 kyc-scanner z-10 top-0"></div>

                {[1, 2, 3].map((i) => (
                    <div key={i} className="kyc-line relative w-full h-1.5 bg-cream/5 rounded-full flex items-center">
                        <div className="kyc-fill kyc-reset h-full bg-sage rounded-full w-0"></div>
                        <div className="kyc-status kyc-reset absolute right-2 text-[6px] text-sage opacity-0 font-bold translate-x-[120%]">OK</div>
                    </div>
                ))}
                <div className="kyc-line relative w-3/4 h-1.5 bg-cream/5 rounded-full flex items-center">
                    <div className="kyc-fill kyc-reset h-full bg-sage rounded-full w-0"></div>
                    <div className="kyc-status kyc-reset absolute right-2 text-[6px] text-sage opacity-0 font-bold translate-x-[120%]">OK</div>
                </div>

                <div className="kyc-approved kyc-reset absolute inset-0 bg-ink/85 backdrop-blur-[1px] flex items-center justify-center opacity-0 scale-90 z-20">
                    <span className="border-2 border-sage text-sage px-3 py-1 font-bold text-[10px] rotate-[-5deg] rounded">VERIFIED</span>
                </div>
            </div>
        </div>
    );
}

function TelecomCard() {
    const netRef = useRef(null);
    const D = useDemoColors();

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            tl.to(".net-flow-1", { strokeDashoffset: -40, duration: 1, ease: "none" })
                .to(".net-flow-2", { strokeDashoffset: -40, duration: 1, ease: "none" }, 0)
                .to(".net-node-3", { fill: D.ember, scale: 1.2, duration: 0.2, repeat: 3, yoyo: true, transformOrigin: "center" }, 1)
                .fromTo(".net-heal", { scale: 0, opacity: 1 }, { scale: 3, opacity: 0, duration: 1, transformOrigin: "center" }, 2)
                .to(".net-node-3", { fill: D.sage, scale: 1, duration: 0.3 }, 2.5);
        }, netRef);
        return () => ctx.revert();
    }, [D]);

    return (
        <div ref={netRef} className="relative mt-8 w-full h-44 flex items-center justify-center overflow-hidden rounded-2xl border border-cream/10 bg-cream/[0.02] p-4 font-['IBM_Plex_Mono']">
            <div className="absolute top-3 right-4 text-[9px] uppercase tracking-widest text-ember font-bold z-20 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-ember rounded-full animate-pulse"></span> Ops Agent
            </div>

            <svg viewBox="0 0 200 100" className="w-full h-full relative z-10 overflow-visible">
                <line x1="40" y1="50" x2="100" y2="20" stroke={D.tint(0.12)} strokeWidth="2" />
                <line x1="40" y1="50" x2="100" y2="80" stroke={D.tint(0.12)} strokeWidth="2" />
                <line x1="100" y1="20" x2="160" y2="50" stroke={D.tint(0.12)} strokeWidth="2" />
                <line x1="100" y1="80" x2="160" y2="50" stroke={D.tint(0.12)} strokeWidth="2" />

                <line className="net-flow-1" x1="40" y1="50" x2="100" y2="20" stroke={D.ember} strokeWidth="2" strokeDasharray="5 45" strokeDashoffset="0" />
                <line className="net-flow-2" x1="40" y1="50" x2="100" y2="80" stroke={D.sage} strokeWidth="2" strokeDasharray="5 45" strokeDashoffset="0" />

                <circle className="net-heal" cx="160" cy="50" r="10" fill="none" stroke={D.sage} strokeWidth="2" opacity="0" />

                <circle cx="40" cy="50" r="6" fill={D.ink} stroke={D.cream} strokeWidth="1.5" />
                <circle cx="100" cy="20" r="6" fill={D.ink} stroke={D.cream} strokeWidth="1.5" />
                <circle cx="100" cy="80" r="6" fill={D.ink} stroke={D.cream} strokeWidth="1.5" />
                <circle className="net-node-3" cx="160" cy="50" r="8" fill={D.sage} />
            </svg>
        </div>
    );
}

function FieldOpsCard() {
    const fieldRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            gsap.utils.toArray(".field-line").forEach((line) => {
                tl.to(line.querySelector(".field-fill"), { width: "100%", duration: 0.35, ease: "none" })
                    .to(line.querySelector(".field-check"), { opacity: 1, scale: 1, duration: 0.2, ease: "back.out(2)" }, "-=0.1");
            });
            tl.to(".field-routed", { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" })
                .to({}, { duration: 1.5 })
                .to(".field-routed", { opacity: 0, y: 6, duration: 0.2 })
                .to(".field-reset", { width: "0%", duration: 0.1 })
                .to(".field-check", { opacity: 0, scale: 0.6, duration: 0.1 });
        }, fieldRef);
        return () => ctx.revert();
    }, []);

    return (
        <div ref={fieldRef} className="relative mt-8 w-full h-44 flex justify-center items-center overflow-hidden rounded-2xl border border-cream/10 bg-cream/[0.02] p-4 font-['IBM_Plex_Mono']">
            <div className="absolute top-3 left-4 text-[9px] uppercase tracking-widest text-cream/35 font-bold z-20 flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 bg-ember rounded-full animate-pulse"></div> Daily Field Log
            </div>

            <div className="relative w-44 h-28 bg-well border border-cream/10 rounded p-4 flex flex-col gap-2.5 mt-4 z-10">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="field-line relative w-full h-1.5 bg-cream/5 rounded-full flex items-center">
                        <div className="field-fill field-reset h-full bg-sage rounded-full w-0"></div>
                        <div className="field-check field-reset absolute right-2 text-sage opacity-0 scale-[0.6] text-[8px] font-bold">✓</div>
                    </div>
                ))}

                <div className="field-routed absolute inset-x-3 bottom-2 flex items-center justify-between bg-ember/10 border border-ember/25 rounded px-2 py-1 opacity-0 translate-y-1.5">
                    <span className="text-[7px] uppercase tracking-widest text-ember font-bold">Change Order</span>
                    <span className="text-[7px] uppercase tracking-widest text-cream/45 font-bold">→ Approve</span>
                </div>
            </div>
        </div>
    );
}

const INDUSTRIES = [
    {
        num: "01",
        title: "Manufacturing & Logistics",
        bullets: ["Vision QA with agentic rework", "Supplier-invoice (AP) automation", "Inbound document intake & exception routing"],
        Demo: ManufacturingCard,
    },
    {
        num: "02",
        title: "Energy & Utilities",
        bullets: ["Video-based asset & emissions inspection", "Predictive-maintenance work-order routing", "Hands-free field operations agents"],
        Demo: EnergyCard,
    },
    {
        num: "03",
        title: "Public Sector & Smart Cities",
        bullets: ["Citizen-service assistants grounded in policy", "Permit processing with officer approval gates", "Video search & auditable escalation"],
        Demo: PublicSectorCard,
    },
    {
        num: "04",
        title: "Financial Services & Insurance",
        bullets: ["Underwriting decision agents traced to the rate card", "KYC & claims Document AI", "Reconciliation & audit-prep agents"],
        Demo: FinanceCard,
    },
    {
        num: "05",
        title: "Telecom & Tech",
        bullets: ["Network-ops exception agents with Sev-1 escalation", "Customer-care automation with measured deflection", "Field-tech voice agents for install & repair"],
        Demo: TelecomCard,
    },
    {
        num: "06",
        title: "Real Estate, AEC & Field Services",
        bullets: ["Vision estimating on a governed price book", "Daily field capture & change-order approvals", "Inspections, measurements & work-order coordination"],
        Demo: FieldOpsCard,
    },
];

function Industries() {
    const [sel, setSel] = useState(0);
    const isDesktop = useMedia("(min-width: 1024px)");
    const rootRef = useRef(null);
    const panelRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(
                ".ind-row, .ind-mobile-card",
                { y: 40, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.9, stagger: 0.08, ease: "power3.out", scrollTrigger: { trigger: rootRef.current, start: "top 78%" } }
            );
        }, rootRef);
        return () => ctx.revert();
    }, [isDesktop]);

    useLayoutEffect(() => {
        if (REDUCED() || !panelRef.current) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(".ind-panel", { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: "power3.out" });
        }, panelRef);
        return () => ctx.revert();
    }, [sel, isDesktop]);

    const active = INDUSTRIES[sel];

    return (
        <section ref={rootRef} id="industries" className="relative py-24 md:py-36 px-6 max-w-7xl mx-auto border-t border-cream/5">
            <SectionHead num="02" eyebrow="Industries" title="Sector-specific AI systems." italicWords={["systems."]} />

            {isDesktop ? (
                <div className="grid lg:grid-cols-[1.15fr_1fr] gap-14 xl:gap-20 items-start">
                    <div role="tablist" aria-label="Industries">
                        {INDUSTRIES.map((ind, i) => (
                            <button
                                key={ind.num}
                                role="tab"
                                aria-selected={sel === i}
                                onClick={() => setSel(i)}
                                className="ind-row group w-full text-left py-6 border-b border-cream/10 flex items-baseline gap-6 cursor-pointer"
                            >
                                <span className={`font-['IBM_Plex_Mono'] text-sm transition-colors duration-300 ${sel === i ? "text-ember" : "text-cream/30"}`}>
                                    {ind.num}
                                </span>
                                <span
                                    className={`text-2xl xl:text-3xl font-['Space_Grotesk'] font-bold tracking-tight transition-colors duration-300 ${
                                        sel === i ? "text-cream" : "text-cream/35 group-hover:text-cream/70"
                                    }`}
                                >
                                    {ind.title}
                                </span>
                                <ArrowUpRight
                                    className={`w-5 h-5 ml-auto shrink-0 self-center transition-all duration-300 ${
                                        sel === i ? "text-ember opacity-100" : "opacity-0 group-hover:opacity-40 text-cream"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>

                    <div ref={panelRef} className="sticky top-28">
                        <div key={sel} className="ind-panel bg-onyx border border-cream/10 rounded-[2rem] p-8">
                            <div className="flex items-baseline gap-4 mb-5">
                                <span className="font-['IBM_Plex_Mono'] text-ember font-bold text-sm">{active.num}</span>
                                <h3 className="text-xl font-bold text-cream font-['Space_Grotesk']">{active.title}</h3>
                            </div>
                            <ul className="space-y-2.5 mb-2">
                                {active.bullets.map((b, j) => (
                                    <li key={j} className="text-cream/55 text-sm font-light flex items-center gap-2.5">
                                        <span className="w-1 h-1 bg-sage rounded-full shrink-0"></span>
                                        {b}
                                    </li>
                                ))}
                            </ul>
                            <active.Demo />
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {INDUSTRIES.map((ind) => (
                        <div key={ind.num} className="ind-mobile-card bg-onyx border border-cream/10 rounded-[2rem] p-7 flex flex-col">
                            <span className="font-['IBM_Plex_Mono'] text-ember font-bold mb-3 text-sm">{ind.num}</span>
                            <h3 className="text-xl font-bold text-cream mb-3 font-['Space_Grotesk']">{ind.title}</h3>
                            <ul className="space-y-2 mb-4">
                                {ind.bullets.map((b, j) => (
                                    <li key={j} className="text-cream/55 text-xs font-light flex items-center gap-2">
                                        <span className="w-1 h-1 bg-sage rounded-full shrink-0"></span>
                                        {b}
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-auto">
                                <ind.Demo />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/* ----------------------------------- cases ----------------------------------- */

function Cases() {
    const sectionRef = useRef(null);
    const railRef = useRef(null);
    const progressRef = useRef(null);

    useLayoutEffect(() => {
        const mm = gsap.matchMedia();

        mm.add("(min-width: 1024px) and (prefers-reduced-motion: no-preference)", () => {
            const rail = railRef.current;
            const getScroll = () => Math.max(0, rail.scrollWidth - window.innerWidth);

            const tween = gsap.to(rail, {
                x: () => -getScroll(),
                ease: "none",
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top top",
                    end: () => "+=" + getScroll(),
                    pin: true,
                    scrub: 1,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    onUpdate: (self) => {
                        if (progressRef.current) gsap.set(progressRef.current, { scaleX: self.progress });
                    },
                },
            });

            gsap.utils.toArray(".case-card").forEach((card) => {
                gsap.fromTo(
                    card,
                    { opacity: 0.25, scale: 0.94 },
                    {
                        opacity: 1,
                        scale: 1,
                        ease: "none",
                        scrollTrigger: { trigger: card, containerAnimation: tween, start: "left 95%", end: "left 55%", scrub: true },
                    }
                );
            });
        });

        mm.add("(max-width: 1023.98px) and (prefers-reduced-motion: no-preference)", () => {
            gsap.utils.toArray(".case-card").forEach((card) => {
                gsap.fromTo(
                    card,
                    { y: 44, opacity: 0 },
                    { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: card, start: "top 88%" } }
                );
            });
        });

        return () => mm.revert();
    }, []);

    return (
        <section
            ref={sectionRef}
            id="cases"
            className="relative border-t border-cream/5 bg-ink py-24 lg:py-0 lg:h-screen lg:overflow-hidden lg:flex lg:flex-col lg:justify-center"
        >
            <div className="px-6 lg:px-[6vw] max-w-7xl lg:max-w-none mx-auto lg:mx-0 w-full">
                <div className="lg:flex lg:items-end lg:justify-between lg:gap-12">
                    <SectionHead num="03" eyebrow="Case Studies" title="Measurable impact, in production." italicWords={["production."]} />
                    <div className="hidden lg:block mb-20 w-56">
                        <div className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.3em] text-cream/35 mb-3">Scroll to explore</div>
                        <div className="h-px bg-cream/10 w-full overflow-hidden">
                            <div ref={progressRef} className="h-full w-full origin-left scale-x-0 bg-ember"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div ref={railRef} className="flex flex-col gap-6 px-6 lg:flex-row lg:items-stretch lg:gap-8 lg:px-[6vw] lg:w-max will-change-transform">
                {caseStudies.map((c, i) => (
                    <article
                        key={c.num}
                        className={`case-card card-glow relative shrink-0 w-full lg:w-[62vw] xl:w-[52vw] bg-onyx border rounded-[2rem] p-7 md:p-12 flex flex-col overflow-hidden ${
                            i === 0 ? "border-ember/30" : "border-cream/10"
                        }`}
                    >
                        <div className="mb-5 md:mb-8">
                            <span className="font-['IBM_Plex_Mono'] text-ember font-bold text-base md:text-lg">{c.num}</span>
                        </div>
                        <h3 className="text-2xl md:text-4xl font-bold text-cream mb-4 md:mb-6 tracking-tight leading-tight font-['Space_Grotesk']">
                            {c.href ? <a href={c.href} className="text-cream hover:text-ember transition-colors">{c.title}</a> : c.title}
                        </h3>
                        <p className="text-sm md:text-base text-cream/55 font-light leading-relaxed lg:line-clamp-6 mb-8">{c.summary}</p>

                        <div className="mt-auto grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {c.impact.map((m, j) => (
                                <div key={j} className="px-4 py-4 rounded-xl bg-cream/[0.04] border border-cream/10 flex flex-col justify-center">
                                    <span className="text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-cream/40 mb-1.5 font-bold">{m.k}</span>
                                    <span className="text-lg md:text-2xl font-bold text-sage font-['IBM_Plex_Mono'] leading-tight">{m.v}</span>
                                </div>
                            ))}
                        </div>
                    </article>
                ))}
            </div>
        </section>
    );
}

/* --------------------------------- philosophy --------------------------------- */

function Philosophy() {
    const rootRef = useRef(null);
    const { theme } = useTheme();
    const isLight = theme === "light";

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(
                ".phil-w",
                { opacity: 0.1 },
                {
                    opacity: 1,
                    stagger: 0.06,
                    ease: "none",
                    scrollTrigger: { trigger: ".phil-statement", start: "top 78%", end: "top 22%", scrub: true },
                }
            );
            gsap.fromTo(
                ".phil-after",
                { y: 22, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.8, stagger: 0.12, ease: "power3.out", scrollTrigger: { trigger: ".phil-after-wrap", start: "top 85%" } }
            );

            gsap.timeline({ repeat: -1, repeatDelay: 1, scrollTrigger: { trigger: ".ptp-track", start: "top 90%" } })
                .to(".ptp-fill", { width: "100%", duration: 2, ease: "power1.inOut" })
                .to(".ptp-dot", { left: "100%", duration: 2, ease: "power1.inOut" }, "<")
                .to(".ptp-pilot-label", { opacity: 0.35, duration: 0.4 }, "<")
                .to(".ptp-prod-label", { opacity: 1, duration: 0.4 }, "-=0.4")
                .to({}, { duration: 1.6 })
                .to(".ptp-fill", { width: "0%", duration: 0.7, ease: "power1.in" })
                .to(".ptp-dot", { left: "0%", duration: 0.7, ease: "power1.in" }, "<")
                .to(".ptp-prod-label", { opacity: 0.35, duration: 0.4 }, "<")
                .to(".ptp-pilot-label", { opacity: 1, duration: 0.4 }, "-=0.4");
        }, rootRef);
        return () => ctx.revert();
    }, []);

    const s1 = "Most enterprise AI stalls at a pilot.".split(" ");
    const s2 = "We shipped to".split(" ");

    return (
        <section ref={rootRef} id="philosophy" className="relative py-32 md:py-48 bg-onyx border-y border-cream/5 overflow-hidden">
            <div className={`absolute -top-40 left-1/4 w-[36rem] h-[36rem] rounded-full blur-[160px] pointer-events-none ${isLight ? "bg-moss/8" : "bg-moss/30"}`} aria-hidden="true"></div>

            <div className="relative z-10 max-w-5xl mx-auto px-6">
                <div className="flex items-center gap-3 mb-10 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em]">
                    <span className="text-ember">04</span>
                    <span className="h-px w-10 bg-ember/40"></span>
                    <span className="text-cream/50">Philosophy</span>
                </div>

                <div className="phil-statement">
                    <p className="text-2xl md:text-4xl font-light text-cream/70 leading-snug mb-6 font-['Outfit'] max-w-3xl">
                        {s1.map((w, i) => (
                            <span key={i} className="phil-w inline-block">
                                {w}&nbsp;
                            </span>
                        ))}
                    </p>
                    <h2 className="text-4xl md:text-7xl font-bold text-cream leading-[1.02] tracking-tight font-['Space_Grotesk'] mb-14">
                        {s2.map((w, i) => (
                            <span key={i} className="phil-w inline-block">
                                {w}&nbsp;
                            </span>
                        ))}
                        <span className="phil-w inline-block font-['Cormorant_Garamond'] italic font-medium text-ember">production.</span>
                    </h2>
                </div>

                <div className="phil-after-wrap">
                    <div className="phil-after ptp-track max-w-md mb-12 font-['IBM_Plex_Mono']">
                        <div className="flex justify-between items-center mb-3 text-[10px] uppercase tracking-widest font-bold">
                            <span className="ptp-pilot-label text-cream/60">Pilot</span>
                            <span className="ptp-prod-label text-ember opacity-35">Production</span>
                        </div>
                        <div className="relative h-1.5 w-full bg-cream/10 rounded-full overflow-hidden">
                            <div className="ptp-fill absolute inset-y-0 left-0 w-0 bg-gradient-to-r from-sage to-ember rounded-full"></div>
                            <div className="ptp-dot absolute top-1/2 -translate-y-1/2 -translate-x-1/2 left-0 w-3 h-3 rounded-full bg-ember shadow-[0_0_12px_rgba(232,102,60,0.7)]"></div>
                        </div>
                    </div>

                    <p className="phil-after text-lg md:text-xl font-light text-cream/65 max-w-xl leading-relaxed font-['Outfit'] mb-8">
                        Every agent we ship drafts the decision. A person signs off before anything consequential happens.
                    </p>

                    <p className="phil-after flex items-center gap-2 text-cream/45 text-xs font-['IBM_Plex_Mono'] uppercase tracking-widest">
                        <span className="w-1.5 h-1.5 shrink-0 rounded-full bg-ember animate-pulse"></span>
                        <span>
                            One example:{" "}
                            <a
                                href="https://bldify.com"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-ember underline underline-offset-4 hover:text-ember/80 transition-colors normal-case"
                            >
                                bldify.com
                            </a>
                            , built and shipped by this team.
                        </span>
                    </p>
                </div>
            </div>
        </section>
    );
}

/* --------------------------------- methodology --------------------------------- */

const METHOD_STEPS = [
    {
        num: "01",
        title: "Strategy Sprint",
        window: "Weeks 0–2",
        desc: "Executive alignment and opportunity mapping in structured, high-velocity discovery cycles.",
    },
    {
        num: "02",
        title: "Pilot & POV",
        window: "Weeks 2–8",
        desc: "Rapid deployment of focused proof-of-values to completely de-risk enterprise investments.",
    },
    {
        num: "03",
        title: "Scale & Operate",
        window: "Quarter 1+",
        desc: "Enterprise-wide rollout with sustained governance, workflow integration, and constant optimization.",
    },
];

function Methodology() {
    const rootRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(
                ".meth-line",
                { scaleY: 0 },
                {
                    scaleY: 1,
                    ease: "none",
                    scrollTrigger: { trigger: ".meth-steps", start: "top 75%", end: "bottom 55%", scrub: true },
                }
            );
            gsap.utils.toArray(".meth-step").forEach((step, i) => {
                gsap.fromTo(
                    step,
                    { x: i % 2 ? 44 : -44, opacity: 0 },
                    { x: 0, opacity: 1, duration: 0.9, ease: "power3.out", scrollTrigger: { trigger: step, start: "top 82%" } }
                );
            });
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <section ref={rootRef} id="methodology" className="relative py-24 md:py-36 px-6 max-w-7xl mx-auto">
            <SectionHead num="05" eyebrow="Methodology" title="Three moves from idea to impact." italicWords={["impact."]} />

            <div className="meth-steps relative">
                <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-cream/10" aria-hidden="true">
                    <div className="meth-line absolute inset-0 origin-top bg-gradient-to-b from-sage to-ember"></div>
                </div>

                {METHOD_STEPS.map((st, i) => (
                    <div
                        key={st.num}
                        className={`meth-step relative pl-14 pb-16 lg:pb-24 last:pb-0 lg:w-1/2 ${
                            i % 2 ? "lg:ml-auto lg:pl-16" : "lg:pl-0 lg:pr-16 lg:text-right"
                        }`}
                    >
                        <span
                            className={`absolute top-2 left-4 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-ember bg-ink ${
                                i % 2 ? "lg:left-0" : "lg:left-auto lg:right-0 lg:translate-x-1/2"
                            }`}
                            aria-hidden="true"
                        ></span>
                        <div className="font-['IBM_Plex_Mono'] text-xs text-ember font-bold mb-3 uppercase tracking-[0.25em]">
                            {st.num} · {st.window}
                        </div>
                        <h3 className="text-3xl md:text-5xl font-bold text-cream tracking-tight mb-4 font-['Space_Grotesk']">{st.title}</h3>
                        <p className={`text-base md:text-lg font-light text-cream/55 leading-relaxed max-w-md ${i % 2 ? "" : "lg:ml-auto"}`}>{st.desc}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ------------------------------- about mini cards ------------------------------- */

function MiniShell({ label, value, valueClass = "text-sage", children, refProp }) {
    return (
        <div ref={refProp} className="relative mt-6 w-full h-28 overflow-hidden rounded-xl border border-cream/10 bg-cream/[0.03] p-2.5 font-['IBM_Plex_Mono']">
            <div className="flex justify-between items-center gap-2 mb-1">
                <span className="text-[8px] uppercase tracking-wide text-cream/35 font-bold whitespace-nowrap">{label}</span>
                <span className={`text-[9px] font-bold ${valueClass}`}>{value}</span>
            </div>
            {children}
        </div>
    );
}

function NasaMiniCard() {
    const ref = useRef(null);
    const D = useDemoColors();

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.to(".nasa-orbit", { rotation: 360, duration: 5, repeat: -1, ease: "none", transformOrigin: "50px 34px" });
        }, ref);
        return () => ctx.revert();
    }, []);

    return (
        <MiniShell refProp={ref} label="Telemetry" value="Nominal">
            <div className="relative h-[4.5rem] flex items-center justify-center">
                <svg viewBox="0 0 100 68" className="w-full h-full overflow-visible">
                    <circle cx="50" cy="34" r="26" fill="none" stroke={D.tint(0.1)} />
                    <circle cx="50" cy="34" r="16" fill="none" stroke={D.tint(0.1)} />
                    <g className="nasa-orbit">
                        <g transform="translate(50 8)">
                            <path d="M0 -7 L2.4 -1 L2.4 4 L-2.4 4 L-2.4 -1 Z" fill={D.sage} />
                            <circle cx="0" cy="-1" r="1" fill={D.ink} />
                            <path d="M-2.4 4 L-3.6 7.5 L-1 4.8 Z" fill={D.ember} />
                            <path d="M2.4 4 L3.6 7.5 L1 4.8 Z" fill={D.ember} />
                        </g>
                    </g>
                    <circle cx="50" cy="34" r="1.5" fill={D.cream} />
                </svg>
            </div>
        </MiniShell>
    );
}

function NvidiaMiniCard() {
    const ref = useRef(null);
    const valRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.utils.toArray(".gpu-bar").forEach((bar, i) => {
                gsap.to(bar, {
                    scaleY: 0.35 + Math.random() * 0.65,
                    duration: 0.5 + Math.random() * 0.6,
                    repeat: -1,
                    yoyo: true,
                    ease: "sine.inOut",
                    transformOrigin: "bottom",
                    delay: i * 0.08,
                });
            });
        }, ref);
        const interval = setInterval(() => {
            if (valRef.current) valRef.current.innerText = Math.floor(70 + Math.random() * 25) + "%";
        }, 550);
        return () => {
            ctx.revert();
            clearInterval(interval);
        };
    }, []);

    return (
        <div ref={ref} className="relative mt-6 w-full h-28 overflow-hidden rounded-xl border border-cream/10 bg-cream/[0.03] p-2.5 font-['IBM_Plex_Mono']">
            <div className="flex justify-between items-center gap-2 mb-2">
                <span className="text-[8px] uppercase tracking-wide text-cream/35 font-bold whitespace-nowrap">GPU Cluster</span>
                <span ref={valRef} className="text-[9px] font-bold text-ember">82%</span>
            </div>
            <div className="flex items-end justify-center gap-1.5 h-14 px-1">
                {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="gpu-bar w-2 bg-sage rounded-sm h-full" style={{ transformOrigin: "bottom" }}></div>
                ))}
            </div>
        </div>
    );
}

function MercedesMiniCard() {
    const ref = useRef(null);
    const valRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.to(".mb-needle", { left: "68%", duration: 1.5, repeat: -1, yoyo: true, ease: "sine.inOut" });
        }, ref);
        const interval = setInterval(() => {
            if (valRef.current) valRef.current.innerText = "±0.0" + Math.floor(14 + Math.random() * 9) + "mm";
        }, 500);
        return () => {
            ctx.revert();
            clearInterval(interval);
        };
    }, []);

    return (
        <div ref={ref} className="relative mt-6 w-full h-28 overflow-hidden rounded-xl border border-cream/10 bg-cream/[0.03] p-2.5 font-['IBM_Plex_Mono']">
            <div className="flex justify-between items-center gap-2 mb-2">
                <span className="text-[8px] uppercase tracking-wide text-cream/35 font-bold whitespace-nowrap">QA Tolerance</span>
                <span ref={valRef} className="text-[9px] font-bold text-sage">
                    ±0.018mm
                </span>
            </div>
            <div className="relative h-14 flex flex-col items-center justify-center gap-2.5">
                <div className="relative w-full h-2.5 rounded-full bg-cream/5">
                    <div className="absolute inset-y-0 left-[32%] right-[32%] bg-sage/20 rounded-full"></div>
                    <div className="mb-needle absolute -top-1 -bottom-1 left-[50%] w-[3px] -translate-x-1/2 bg-ember rounded-full shadow-[0_0_6px_rgba(232,102,60,0.5)]"></div>
                </div>
                <div className="flex justify-between w-full text-[7px] text-cream/30 font-bold px-0.5 tracking-wider">
                    <span>−0.05</span>
                    <span>SPEC</span>
                    <span>+0.05</span>
                </div>
            </div>
        </div>
    );
}

function ConsultingMiniCard() {
    const ref = useRef(null);
    const D = useDemoColors();
    const steps = ["Discovery", "Design", "Delivery"];

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const tl = gsap.timeline({ repeat: -1 });
            gsap.utils.toArray(".eng-step").forEach((step) => {
                tl.to(step.querySelector(".eng-check"), { opacity: 1, scale: 1, duration: 0.25, ease: "back.out(2)" })
                    .to(step.querySelector(".eng-label"), { opacity: 1, duration: 0.2 }, "<")
                    .to({}, { duration: 0.4 });
            });
            tl.to({}, { duration: 1.2 }).set(".eng-check", { opacity: 0, scale: 0.4 }).set(".eng-label", { opacity: 0.35 });
        }, ref);
        return () => ctx.revert();
    }, []);

    return (
        <MiniShell refProp={ref} label="Engagement" value="On Track" valueClass="text-ember">
            <div className="flex flex-col gap-2.5 mt-1">
                {steps.map((s, i) => (
                    <div key={i} className="eng-step flex items-center gap-2">
                        <div className="eng-check w-3 h-3 shrink-0 rounded-full bg-sage flex items-center justify-center opacity-0 scale-[0.4]">
                            <svg viewBox="0 0 10 10" className="w-2 h-2">
                                <path d="M2 5 L4 7 L8 3" stroke={D.ink} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <span className="eng-label text-[9px] text-cream font-bold opacity-35">{s}</span>
                    </div>
                ))}
            </div>
        </MiniShell>
    );
}

function RoboticsMiniCard() {
    const ref = useRef(null);
    const D = useDemoColors();
    const upperRef = useRef(null);
    const foreRef = useRef(null);
    const clawARef = useRef(null);
    const clawBRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            const state = { upper: 0, fore: 0, clawA: 0, clawB: 0 };
            const apply = () => {
                upperRef.current?.setAttribute("transform", `rotate(${state.upper} 20 42)`);
                foreRef.current?.setAttribute("transform", `rotate(${state.fore} 20 14)`);
                clawARef.current?.setAttribute("transform", `rotate(${state.clawA})`);
                clawBRef.current?.setAttribute("transform", `rotate(${state.clawB})`);
            };

            const tl = gsap.timeline({ repeat: -1, onUpdate: apply });
            tl.to(state, { upper: -38, duration: 1, ease: "power2.inOut" })
                .to(state, { fore: 30, duration: 1, ease: "power2.inOut" }, "<")
                .to(state, { clawA: 18, duration: 0.3, ease: "power1.out" })
                .to(state, { clawB: -18, duration: 0.3, ease: "power1.out" }, "<")
                .to({}, { duration: 0.5 })
                .to(state, { clawA: 0, duration: 0.3, ease: "power1.in" })
                .to(state, { clawB: 0, duration: 0.3, ease: "power1.in" }, "<")
                .to(state, { upper: 0, duration: 1, ease: "power2.inOut" })
                .to(state, { fore: 0, duration: 1, ease: "power2.inOut" }, "<")
                .to({}, { duration: 0.4 });

            apply();
        }, ref);
        return () => ctx.revert();
    }, []);

    return (
        <MiniShell refProp={ref} label="Arm Control" value="Active">
            <div className="relative h-14 overflow-hidden rounded-lg bg-well">
                <div className={`absolute inset-0 ${DEMO_GRID} bg-[size:10px_10px]`}></div>
                <svg viewBox="0 0 100 56" className="absolute inset-0 w-full h-full overflow-visible">
                    <rect x="9" y="44" width="22" height="6" rx="1.5" fill={D.cream} opacity="0.35" />
                    <circle cx="20" cy="42" r="4.5" fill={D.sage} stroke={D.cream} strokeOpacity="0.25" strokeWidth="1" />
                    <g ref={upperRef}>
                        <line x1="20" y1="42" x2="20" y2="14" stroke={D.sage} strokeWidth="3.5" strokeLinecap="round" />
                        <g ref={foreRef}>
                            <line x1="20" y1="14" x2="48" y2="14" stroke={D.sage} strokeWidth="3" strokeLinecap="round" />
                            <circle cx="20" cy="14" r="3.2" fill={D.sage} />
                            <circle cx="20" cy="14" r="1" fill={D.ink} />
                            <g transform="translate(48 14)">
                                <circle cx="0" cy="0" r="2.2" fill={D.sage} />
                                <g ref={clawARef}>
                                    <line x1="0" y1="0" x2="9" y2="-6" stroke={D.ember} strokeWidth="2.5" strokeLinecap="round" />
                                </g>
                                <g ref={clawBRef}>
                                    <line x1="0" y1="0" x2="9" y2="6" stroke={D.ember} strokeWidth="2.5" strokeLinecap="round" />
                                </g>
                            </g>
                        </g>
                    </g>
                </svg>
            </div>
        </MiniShell>
    );
}

function StartupMiniCard() {
    const ref = useRef(null);
    const valRef = useRef(null);
    const D = useDemoColors();

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(".startup-path", { strokeDashoffset: 200 }, { strokeDashoffset: 0, duration: 3, ease: "none", repeat: -1 });
        }, ref);
        const interval = setInterval(() => {
            if (valRef.current) valRef.current.innerText = "+" + Math.floor(30 + Math.random() * 20) + "%";
        }, 700);
        return () => {
            ctx.revert();
            clearInterval(interval);
        };
    }, []);

    return (
        <div ref={ref} className="relative mt-6 w-full h-28 overflow-hidden rounded-xl border border-cream/10 bg-cream/[0.03] p-2.5 font-['IBM_Plex_Mono']">
            <div className="flex justify-between items-center gap-2 mb-1">
                <span className="text-[8px] uppercase tracking-wide text-cream/35 font-bold whitespace-nowrap">MoM Growth</span>
                <span ref={valRef} className="text-[9px] font-bold text-ember">
                    +38%
                </span>
            </div>
            <div className="relative h-14">
                <svg viewBox="0 0 140 56" className="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none">
                    <path d="M0,50 L140,50" stroke={D.tint(0.1)} strokeWidth="1" />
                    <path
                        className="startup-path"
                        d="M0,50 Q20,48 35,40 T70,28 T105,15 T140,6"
                        fill="none"
                        stroke={D.sage}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeDasharray="200"
                        strokeDashoffset="200"
                    />
                </svg>
            </div>
        </div>
    );
}

function About() {
    const rootRef = useRef(null);

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(
                ".about-copy",
                { y: 18, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", scrollTrigger: { trigger: rootRef.current, start: "top 72%" } }
            );
            gsap.fromTo(
                ".about-badge",
                { y: 30, opacity: 0, scale: 0.97 },
                {
                    y: 0,
                    opacity: 1,
                    scale: 1,
                    duration: 0.7,
                    stagger: 0.08,
                    ease: "power3.out",
                    scrollTrigger: { trigger: ".about-grid", start: "top 82%" },
                }
            );
        }, rootRef);
        return () => ctx.revert();
    }, []);

    const badges = [
        { org: "NASA", note: "Mission-critical systems engineering", Anim: NasaMiniCard },
        { org: "NVIDIA", note: "GPU & AI infrastructure at scale", Anim: NvidiaMiniCard },
        { org: "Mercedes-Benz", note: "Automotive-grade physical systems", Anim: MercedesMiniCard },
        { org: "Big 4 Management Consulting", note: "Enterprise consulting & delivery rigor", Anim: ConsultingMiniCard },
        { org: "SV AI & Robotics Startups", note: "Physical AI & robotics product velocity", Anim: RoboticsMiniCard },
        { org: "Enterprise Startups", note: "0→1 enterprise SaaS velocity", Anim: StartupMiniCard },
    ];

    return (
        <section ref={rootRef} id="about" className="relative py-24 md:py-36 px-6 max-w-7xl mx-auto border-t border-cream/5">
            <SectionHead num="06" eyebrow="About Us" title="Built by operators who've shipped at scale." italicWords={["scale."]} />

            <p className="about-copy text-lg md:text-xl text-cream/55 max-w-2xl leading-relaxed font-['Outfit'] -mt-6 mb-14">
                SVC Group is run by engineers and operators who built and shipped systems at NASA, NVIDIA, and Mercedes-Benz, and
                scaled product at high-growth Silicon Valley startups. We brought that same production discipline — rigor, audit
                trails, real ownership of outcomes — to enterprise AI.
            </p>

            <div className="about-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                {badges.map((b, i) => (
                    <div
                        key={i}
                        className="about-badge card-glow bg-onyx border border-cream/10 rounded-[2rem] p-6 hover:border-cream/20 hover:-translate-y-1 transition-all duration-300 flex flex-col"
                    >
                        <div className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-widest text-cream/35 mb-2">Veterans of</div>
                        <div className="font-bold text-cream text-lg md:text-xl font-['Space_Grotesk'] mb-1 leading-tight">{b.org}</div>
                        <div className="text-cream/45 text-xs font-light">{b.note}</div>
                        <div className="mt-auto">
                            <b.Anim />
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ----------------------------------- footer ----------------------------------- */

function Footer() {
    const rootRef = useRef(null);
    const { theme } = useTheme();
    const isLight = theme === "light";

    useLayoutEffect(() => {
        if (REDUCED()) return;
        const ctx = gsap.context(() => {
            gsap.fromTo(
                ".cta-el",
                { y: 26, opacity: 0 },
                { y: 0, opacity: 1, duration: 0.9, stagger: 0.1, ease: "power3.out", scrollTrigger: { trigger: ".cta-wrap", start: "top 80%" } }
            );
            gsap.fromTo(
                ".footer-watermark",
                { xPercent: -3 },
                { xPercent: 3, ease: "none", scrollTrigger: { trigger: ".footer-watermark", start: "top bottom", end: "bottom top", scrub: true } }
            );
        }, rootRef);
        return () => ctx.revert();
    }, []);

    return (
        <footer ref={rootRef} id="contact" className="relative bg-ink border-t border-cream/5 overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-ember/60 to-transparent" aria-hidden="true"></div>
            <div className={`absolute -bottom-56 left-1/2 -translate-x-1/2 w-[52rem] h-[52rem] rounded-full blur-[180px] pointer-events-none ${isLight ? "bg-moss/8" : "bg-moss/25"}`} aria-hidden="true"></div>

            <div className="cta-wrap relative z-10 max-w-4xl mx-auto px-6 pt-28 md:pt-40 pb-20 md:pb-28 text-center flex flex-col items-center">
                <div className="cta-el flex items-center gap-3 mb-7 font-['IBM_Plex_Mono'] text-[11px] uppercase tracking-[0.3em] text-cream/50 justify-center">
                    <span className="w-1.5 h-1.5 rounded-full bg-ember animate-pulse"></span>
                    Contact
                </div>
                <h2 className="cta-el text-4xl md:text-7xl font-bold text-cream tracking-tight leading-[1.02] font-['Space_Grotesk'] mb-7">
                    Initialize a <span className="font-['Cormorant_Garamond'] italic font-medium text-ember">Sprint.</span>
                </h2>
                <p className="cta-el text-cream/55 text-base md:text-lg font-light max-w-lg leading-relaxed font-['Outfit'] mb-10">
                    Tell us about your goals, timelines, and technical constraints. We evaluate every inquiry confidentially and
                    respond within 24 hours.
                </p>
                <div className="cta-el">
                    <MagneticButton
                        href="mailto:info@svc.group"
                        aria-label="Email Silicon Valley Consulting Group for Enterprise AI Solutions"
                        className="bg-ember text-[#0A0B09] px-10 py-5 text-sm font-bold uppercase tracking-[0.18em] flex items-center gap-3"
                    >
                        info@svc.group <ArrowRight className="w-4 h-4" />
                    </MagneticButton>
                </div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 pb-10">
                <div className="flex flex-col md:flex-row justify-between items-start gap-12 pb-16 border-b border-cream/10">
                    <div className="max-w-sm">
                        <img
                            src={isLight ? BRAND_LOCKUP_LIGHT_HORIZONTAL : BRAND_LOCKUP_DARK}
                            alt="Silicon Valley Consulting Group - AI Agents & Physical AI"
                            className="w-[280px] max-w-full h-auto mb-6"
                        />
                        <div className="inline-flex items-center gap-3 font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-widest text-sage/90 bg-sage/10 px-4 py-2 rounded-full border border-sage/20">
                            <span className="w-1.5 h-1.5 rounded-full bg-sage animate-[pulse_2s_ease-in-out_infinite]"></span>
                            System Operational
                        </div>
                    </div>

                    <div className="flex gap-16 md:gap-28 flex-wrap">
                        <div className="flex flex-col gap-3.5 text-sm font-['Outfit'] text-cream/60">
                            <h4 className="font-bold text-cream tracking-widest uppercase text-xs mb-2">Capabilities</h4>
                            <a href="/services/ai-agents-automation" className="hover:text-cream transition-colors">AI Agents</a>
                            <a href="/services/company-brain-enterprise-context" className="hover:text-cream transition-colors">Company Brain</a>
                            <a href="/services/ai-infrastructure-ai-factories" className="hover:text-cream transition-colors">AI Factories</a>
                            <a href="/services/ai-data-platforms" className="hover:text-cream transition-colors">Data Platforms</a>
                            <a href="/services/physical-ai-robotics" className="hover:text-cream transition-colors">Physical AI</a>
                        </div>
                        <div className="flex flex-col gap-3.5 text-sm font-['Outfit'] text-cream/60">
                            <h4 className="font-bold text-cream tracking-widest uppercase text-xs mb-2">Company</h4>
                            <a href="/work/" className="hover:text-cream transition-colors">Work</a>
                            <a href="/insights/" className="hover:text-cream transition-colors">Insights</a>
                            <a href="#about" className="hover:text-cream transition-colors">About</a>
                        </div>
                    </div>
                </div>

                <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-cream/40 font-['IBM_Plex_Mono'] uppercase tracking-widest">
                    <span>&copy; {new Date().getFullYear()} Silicon Valley Consulting Group</span>
                    <span>Palo Alto · Worldwide</span>
                </div>
            </div>

            <div className="footer-watermark relative z-0 select-none pointer-events-none text-center leading-none font-['Space_Grotesk'] font-bold text-outline text-[17vw] tracking-tight -mb-[3.5vw]" aria-hidden="true">
                SVC GROUP
            </div>
        </footer>
    );
}

/* ------------------------------------ root ------------------------------------ */

export default function WebsiteV3() {
    const [booted, setBooted] = useState(false);
    const [activeId, setActiveId] = useState("top");
    const onBoot = useCallback(() => setBooted(true), []);
    const lenisRef = useRef(null);

    // Smooth scrolling
    useEffect(() => {
        if (REDUCED()) return;
        const lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            orientation: "vertical",
            gestureOrientation: "vertical",
            smoothWheel: true,
            wheelMultiplier: 1,
            touchMultiplier: 2,
        });
        lenisRef.current = lenis;

        lenis.on("scroll", ScrollTrigger.update);
        const raf = (time) => lenis.raf(time * 1000);
        gsap.ticker.add(raf);
        gsap.ticker.lagSmoothing(0, 0);

        return () => {
            lenisRef.current = null;
            lenis.destroy();
            gsap.ticker.remove(raf);
        };
    }, []);

    // Lenis swallows native hash jumps, so route in-page anchors through it
    useEffect(() => {
        const onClick = (e) => {
            const a = e.target.closest && e.target.closest('a[href^="#"]');
            if (!a) return;
            const id = a.getAttribute("href").slice(1);
            const el = id && document.getElementById(id);
            if (!el) return;
            e.preventDefault();
            const lenis = lenisRef.current;
            if (lenis) lenis.scrollTo(el, { offset: 0, duration: 1.4 });
            else el.scrollIntoView();
            window.history.replaceState(null, "", `#${id}`);
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);

    // Re-measure pinned/scrubbed triggers once everything has laid out
    useEffect(() => {
        const onLoad = () => ScrollTrigger.refresh();
        window.addEventListener("load", onLoad);
        if (document.fonts?.ready) document.fonts.ready.then(() => ScrollTrigger.refresh());
        return () => window.removeEventListener("load", onLoad);
    }, []);

    // Keep URL hash + nav highlight in sync with the section in view
    useEffect(() => {
        const sectionIds = ["top", "services", "industries", "cases", "philosophy", "methodology", "about", "contact"];
        const sections = sectionIds.map((id) => document.getElementById(id)).filter(Boolean);

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.find((entry) => entry.isIntersecting);
                if (!visible) return;
                const id = visible.target.id;
                setActiveId(id);
                const nextHash = id === "top" ? "" : `#${id}`;
                if (window.location.hash !== nextHash) {
                    const url = window.location.pathname + window.location.search + nextHash;
                    window.history.replaceState(null, "", url);
                }
            },
            { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
        );

        sections.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <ThemeProvider>
            <div className="v3-selection bg-ink text-cream min-h-screen font-['Outfit'] antialiased overflow-x-clip transition-colors duration-500">
                <NoiseOverlay />
                <CustomCursor />
                <Preloader onDone={onBoot} />
                <Navbar activeId={activeId} />
                <Hero booted={booted} />
                <Marquee />
                <Services />
                <Industries />
                <Cases />
                <Philosophy />
                <Methodology />
                <About />
                <Footer />
            </div>
        </ThemeProvider>
    );
}
